import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import RowDetailsSheet from './RowDetailsSheet';
import { useIsMobile } from './MobileReportView';
import MobileReportPopup from './MobileReportPopup';
import { ReportDocument, printReportDocument, downloadReportCsv, emailReportSummary } from './reports/ReportDriver';
import supabase from '../config/supabase';
import { SUPABASE_URL, SUPABASE_KEY } from '../config/database';
import { getPoEmailStatusInfo } from '../utils/poEmailStatus';
import { resolvePoLinePackSize } from '../utils/poLinePack';
import { applyActiveFilter } from '../utils/sessionLifecycle';
import { reportingSnapshotFromCountItem, unitValuePriceFromCountItem, describeValuation } from '../utils/countItemReporting';
import { calculateOrderQuantity } from '../utils/orderMath';
import {
  buildPrepList,
  groupPrepTasks,
  isPreparedRow,
} from '../utils/preparedReplenishment';
import VendorOrderSendSheet from './VendorOrderSendSheet';
import { previewPurchaseOrder, resendPurchaseOrder } from '../services/purchaseOrderService';
import { formatUserCompact, getCurrentUser, isStoreOwnedBySuperAdmin, isSuperAdminId, SUPER_ADMIN_USER_IDS, COUNTERTOP_APP_CREATOR_LABEL } from '../utils/userDisplay';
import {
  buildVendorContactInfo,
  getVendorSendReadiness,
  loadVendorDirectory,
  normalizeVendorLabel,
  resolveVendorForName,
} from '../utils/vendorContacts';
import EOPWizard from './EOPWizard';
import PNLCategoriesSettings from './PNLCategoriesSettings';
import PNLSetupWizard, { hasInProgressWizard } from './PNLSetupWizard';
import { SessionTimelineStack } from './SessionTimelineStack';
import ReportModal from './ReportModal';
import RedesignedPriceChangeReportDisplay from './PriceChangeReportDisplay';
import {
  buildPriceChangeReportData,
  PRICE_CHANGE_HISTORY_SELECT,
  PRICE_CHANGE_ITEM_SELECT,
  loadPriceChangeCategoryMappings,
  summarizePriceChangeGroups,
} from '../utils/priceChangeReport.mjs';

const REPORT_TYPES = [
  {
    id: 'detail',
    title: 'Inventory Detail',
    icon: '📋',
    description: 'Select one to many counts and review item-level totals.'
  },
  {
    id: 'value',
    title: 'Inventory Value',
    icon: '💰',
    description: 'Category totals and full inventory valuation by count set.'
  },
  {
    id: 'order',
    title: 'Order Report',
    icon: '🛒',
    description: 'Order value grouped by vendor with contact details.'
  },
  {
    id: 'cogs',
    title: 'End of Period',
    icon: '📊',
    description: 'End of period COGS report per P&L category with trend graph.'
  },
  {
    id: 'item-price-change',
    title: 'Price Change Report',
    icon: '📈',
    description: 'Review actual item price movements by item, vendor, and category.'
  },
  {
    id: 'theoretical-food-cost',
    title: 'Theoretical Food Cost',
    icon: '🍽️',
    description: 'What the items you sold should have cost, based on POS product mix and completed plate costs.'
  },
  {
    id: 'prep',
    title: 'Prep Report',
    icon: '🥣',
    description: 'What to prepare in house, with each sub-recipe\'s batch cost and the value of the prep it produces.'
  }
];

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

const formatCurrency = (value) => currencyFormatter.format(Number.isFinite(value) ? value : 0);
const formatNumber = (value) => numberFormatter.format(Number.isFinite(value) ? value : 0);

// Theoretical food cost arrives from the menu-recipe-costing edge function at
// full precision with nulls for genuinely absent figures — format for display
// here only (mirror of moneyOrDash/pctOrDash in utils/eopReportDescriptor.js).
const toFiniteNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const moneyOrDash = (value) =>
  (value == null || value === '' || !Number.isFinite(Number(value)) ? '—' : formatCurrency(Number(value)));
const pctOrDash = (value) =>
  (value == null || !Number.isFinite(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`);

// Sub-dime unit costs keep up to 4 decimals — a $0.1049/fl oz prepared-item
// cost is meaningless at 2, and rounding it to $0.10 loses 5% of the number.
// Mirrors formatMoney({precise:true}) in utils/itemCostSummary.js.
const formatMoneyPrecise = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) < 0.1) return `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
  return formatCurrency(n);
};

const THEORETICAL_FOOD_COST_EXPLANATION = 'Theoretical food cost estimates what the ingredients used for the items sold should have cost, based on your POS product mix and completed plate costs. It does not include actual waste, overportioning, spoilage, theft or inventory variance.';

// Calls the menu-recipe-costing edge function (POS report listing + theoretical
// food cost). Mirrors the eop-pos-import fetch pattern: bearer session token +
// publishable apikey, { error } body on non-200.
const postMenuRecipeCosting = async (payload) => {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session?.access_token) throw new Error('Not authenticated. Please sign in and try again.');
  const resp = await fetch(`${SUPABASE_URL}/functions/v1/menu-recipe-costing`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${sessionData.session.access_token}`,
      apikey: SUPABASE_KEY,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `Report request failed (${resp.status}).`);
  return data;
};

const toReadableLabel = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (match) => match.toUpperCase());

const formatDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  const day = parsed.getDate();
  const suffix = [11, 12, 13].includes(day % 100) ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
  const month = parsed.toLocaleDateString('en-US', { month: 'long' });
  const year = parsed.getFullYear();
  return `${month} ${day}${suffix}, ${year}`;
};

const formatWrittenDate = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  const day = parsed.getDate();
  const suffix = [11, 12, 13].includes(day % 100) ? 'th' : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
  const month = parsed.toLocaleDateString('en-US', { month: 'long' });
  const year = parsed.getFullYear();
  return `${month} ${day}${suffix} ${year}`;
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleString();
};

const countDateFromSession = (session) => session?.completed_at || session?.session_date || session?.created_at || null;

const formatTime12h = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const dayGapBetween = (dateA, dateB) => {
  if (!dateA || !dateB) return null;
  const a = new Date(dateA);
  const b = new Date(dateB);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  // Use whole-day floor semantics to avoid rounding drift for partial-day gaps.
  return Math.floor(Math.abs(a - b) / (1000 * 60 * 60 * 24));
};

const isOrderSession = (session) => session?.order_calculations_enabled === true;

const DELIVERY_STATUS_LABELS = {
  sent: 'Sent by email',
  delivered: 'Sent by email',
  opened: 'Sent by email',
  clicked: 'Sent by email',
  pending_approval: 'Action needed — pending approval',
  blocked: 'Not sent — vendor has no contact email',
  blocked_no_email: 'Marked as sent outside Countertop',
  sent_outside_countertop: 'Marked as sent outside Countertop',
  bounced: 'Action needed — email bounced',
  complained: 'Action needed — complaint received',
  sending: 'Sending',
  draft: 'Draft',
  error: 'Action needed',
};

const NO_DELIVERY_RECORD_STATUSES = new Set(['blocked_no_email', 'sent_outside_countertop']);

const getDeliveryStatusLabel = (status) => DELIVERY_STATUS_LABELS[status] || toReadableLabel(status || 'unknown');

const isLikelyDateField = (fieldName) => /(_at|_date|date$|updated|created)/i.test(String(fieldName || ''));

const formatFieldValue = (value, fieldName = '') => {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (/price|value|cost|amount/i.test(fieldName)) return formatCurrency(value);
    return formatNumber(value);
  }
  if (typeof value === 'string') {
    if (isLikelyDateField(fieldName)) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString();
    }
    return value;
  }
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const csvEscape = (value) => {
  const normalized = String(value ?? '');
  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
};

const storeNameFromLocalStorage = () => {
  try {
    const currentStore = localStorage.getItem('currentStore');
    if (currentStore) {
      const parsed = JSON.parse(currentStore);
      if (parsed?.name) return parsed.name;
    }
  } catch (_) { /* no-op */ }
  try {
    const storedStore = localStorage.getItem('selectedStore');
    if (storedStore) {
      const parsed = JSON.parse(storedStore);
      if (parsed?.name) return parsed.name;
    }
  } catch (_) { /* no-op */ }
  return localStorage.getItem('selectedStoreName') || 'Your Store';
};


const MOBILE_REPORT_TITLES = {
  detail: 'Inventory Detail',
  value: 'Inventory Value',
  order: 'Order Report',
  item: 'Item Database',
  cogs: 'End of Period',
};

// Embedded mode (used by the count page): mounts the same report machinery
// without the page header / report-type grid, opens the requested report in
// the modal immediately, preselects the given sessions, and auto-generates
// detail/value/order reports so a count-card pill lands directly on output.
const Reports = ({ embedded = false, initialReportType = null, initialSessionIds = null, onEmbeddedClose = null }) => {
  const [countSessions, setCountSessions] = useState([]);
  const [countTemplates, setCountTemplates] = useState([]);
  const [selectedSessionIds, setSelectedSessionIds] = useState(() => (initialSessionIds || []).map(String));
  const [reportType, setReportType] = useState(initialReportType);
  const [sessionSearch, setSessionSearch] = useState('');
  const [reportData, setReportData] = useState(null);
  const [resultFooterActions, setResultFooterActions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [error, setError] = useState('');
  const [storeId, setStoreId] = useState(null);
  const [storeName, setStoreName] = useState(() => storeNameFromLocalStorage());
  const [userNameMap, setUserNameMap] = useState(() => new Map());
  const [showPNLSettings, setShowPNLSettings] = useState(false);
  const [showPNLWizard, setShowPNLWizard] = useState(false);
  const [expandedTimeline, setExpandedTimeline] = useState(new Set());
  const [expandedMonths, setExpandedMonths] = useState(() => {
    const now = new Date();
    return new Set([`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`]);
  });
  const [priceChangeStartDate, setPriceChangeStartDate] = useState('');
  const [priceChangeEndDate, setPriceChangeEndDate] = useState('');
  const [pcPreFilterMeta, setPcPreFilterMeta] = useState(null);
  const [pcPreFilterLoading, setPcPreFilterLoading] = useState(false);
  const [pcFilterCategory, setPcFilterCategory] = useState('');
  const [pcFilterVendor, setPcFilterVendor] = useState('');
  const [pcFilterPnlCategory, setPcFilterPnlCategory] = useState('');
  const [pcFilterLocation, setPcFilterLocation] = useState('');
  const [posReports, setPosReports] = useState(null);
  const [posReportsLoading, setPosReportsLoading] = useState(false);
  const [selectedPosReportId, setSelectedPosReportId] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const isMobile = useIsMobile();

  useEffect(() => {
    if (reportType !== 'item-price-change') {
      setPcPreFilterMeta(null);
      setPcFilterCategory('');
      setPcFilterVendor('');
      setPcFilterPnlCategory('');
      setPcFilterLocation('');
      return;
    }
    const activeStoreId = storeId || Number(localStorage.getItem('selectedStoreId'));
    if (!activeStoreId) return;
    setPcPreFilterLoading(true);
    (async () => {
      try {
        const [itemsRes, mappings] = await Promise.all([
          supabase.from('inventory_items').select('category,vendor,location').eq('store_id', activeStoreId),
          loadPriceChangeCategoryMappings({ supabase, storeId: activeStoreId }),
        ]);
        const items = itemsRes.data || [];
        const categoryToPnlLocal = new Map(mappings.map((m) => [String(m.inventory_category || '').trim().toLowerCase(), m.pnl_category]));
        const cats = [...new Set(items.map((i) => i.category).filter(Boolean))].sort();
        const vends = [...new Set(items.map((i) => i.vendor).filter(Boolean))].sort();
        const locs = [...new Set(items.map((i) => i.location).filter(Boolean))].sort();
        const pnls = [...new Set(cats.map((c) => categoryToPnlLocal.get(c.trim().toLowerCase())).filter(Boolean))].sort();
        setPcPreFilterMeta({ categories: cats, vendors: vends, locations: locs, pnlCategories: pnls });
      } catch (_) {
      } finally {
        setPcPreFilterLoading(false);
      }
    })();
  }, [reportType, storeId]);

  // Theoretical Food Cost: load the store's imported POS reports once when the
  // config pane opens (before a result exists), so the user can pick which
  // sales period to cost.
  useEffect(() => {
    if (reportType !== 'theoretical-food-cost' || reportData) return undefined;
    const activeStoreId = storeId || Number(localStorage.getItem('selectedStoreId'));
    if (!activeStoreId) {
      setPosReports([]);
      return undefined;
    }
    let cancelled = false;
    setPosReportsLoading(true);
    (async () => {
      try {
        const data = await postMenuRecipeCosting({ action: 'list_pos_reports', store_id: activeStoreId });
        if (cancelled) return;
        const reports = Array.isArray(data?.reports) ? data.reports : [];
        setPosReports(reports);
        const firstReady = reports.find((r) => r.status === 'ready');
        setSelectedPosReportId(firstReady ? String(firstReady.id) : '');
      } catch (listError) {
        if (!cancelled) {
          setPosReports([]);
          setError(listError.message || 'Error loading POS reports.');
        }
      } finally {
        if (!cancelled) setPosReportsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reportType, storeId, reportData]);

  useEffect(() => {
    loadCountSessions();
  }, []);

  // Dashboard P&L mapping nudge links here with ?openPNLSettings=1. Land the
  // user directly on the cogs tab with the settings panel open so they can
  // map subcategories without an extra click.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('openPNLSettings') === '1') {
        setReportType('cogs');
        setShowPNLSettings(true);
        params.delete('openPNLSettings');
        const next = params.toString();
        window.history.replaceState(null, '', window.location.pathname + (next ? '?' + next : ''));
      }
    } catch (_) { /* no-op */ }
  }, []);

  const sessionsById = useMemo(() => {
    const map = new Map();
    countSessions.forEach((session) => map.set(String(session.id), session));
    return map;
  }, [countSessions]);

  const visibleSessions = useMemo(() => {
    const q = sessionSearch.trim().toLowerCase();
    return countSessions.filter((session) => {
      if (reportType === 'order' && !isOrderSession(session)) return false;
      if (reportType === 'purchase-orders') return false;
      if (!q) return true;
      const searchable = [
        session._displayName,
        session.name,
        session.status,
        session.counted_by,
        session.session_date,
        session.completed_at
      ].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(q);
    });
  }, [countSessions, reportType, sessionSearch]);

  // Timeline groups: templates as headers, regular sessions as stacked runs
  const timelineGroups = useMemo(() => {
    const classifySession = (s) => {
      if (!s) return { isOrder: false, isEOP: false };
      const isOrder = s.order_calculations_enabled === true;
      const cp = s.count_purpose || '';
      const im = s.inventory_mode || '';
      const isEOP = !isOrder && (cp === 'period_end' || cp === 'full_inventory' || im === 'Financial' || im === 'Both');
      return { isOrder, isEOP };
    };

    const q = sessionSearch.trim().toLowerCase();
    // Reports consume only completed counts of the matching mode:
    //   'order'          → only completed order-calculation sessions
    //   'detail'/'value' → only completed inventory (non-order) sessions
    //   anything else    → all sessions (purchase-orders / cogs use their own pickers)
    const isReportSelector = reportType === 'detail' || reportType === 'value' || reportType === 'order';
    const filteredSessions = countSessions.filter((session) => {
      if (reportType === 'order' && !isOrderSession(session)) return false;
      if ((reportType === 'detail' || reportType === 'value') && isOrderSession(session)) return false;
      // For the inventory/order report selectors, only completed runs are
      // valid inputs — drop drafts and in-progress runs from the list.
      if (isReportSelector && session.status !== 'completed') return false;
      if (!q) return true;
      const searchable = [session._displayName, session.name, session.status, session.counted_by, session.session_date, session.completed_at].filter(Boolean).join(' ').toLowerCase();
      return searchable.includes(q);
    });

    const groups = new Map();

    // First pass: build groups from sessions (already filtered by search/type)
    filteredSessions.forEach((session) => {
      const groupId = String(session.count_group_id || session.id);
      if (!groups.has(groupId)) {
        const { isOrder, isEOP } = classifySession(session);
        const baseName = (session.name || '').replace(/\s*-\s*(Run\s*#?\d+|Copy\s*\d*).*$/i, '').trim() || session.name || '';
        groups.set(groupId, { groupId, template: null, runs: [], name: baseName, isOrder, isEOP });
      }
      groups.get(groupId).runs.push(session);
    });

    // Second pass: attach templates to existing groups. We intentionally do
    // NOT create empty groups for templates with zero matching runs when the
    // user is on a report selector — an empty template card is never a valid
    // input for an inventory/order report.
    countTemplates.forEach((template) => {
      const groupId = String(template.count_group_id || template.id);
      if (groups.has(groupId)) {
        const group = groups.get(groupId);
        group.template = template;
        group.name = group.runs[0]?.template_name_snapshot || template.name || group.name;
        // Only use the live template's classification when there are no runs —
        // for groups with runs, the run-level snapshot fields take precedence so
        // renaming or reclassifying a template doesn't change historical records.
        if (!group.runs.length) {
          const { isOrder, isEOP } = classifySession(template);
          group.isOrder = isOrder;
          group.isEOP = isEOP;
        }
      } else if (!q && !isReportSelector) {
        const { isOrder, isEOP } = classifySession(template);
        if (reportType === 'order' && !isOrder) return;
        groups.set(groupId, { groupId, template, runs: [], name: template.name || '', isOrder, isEOP });
      }
    });

    groups.forEach((group) => {
      if (!group.template && group.runs.length > 0) {
        const latestRun = group.runs[0];
        if (latestRun.template_name_snapshot) {
          group.name = latestRun.template_name_snapshot;
        }
      }
    });

    // Sort runs newest-first within each group
    groups.forEach((group) => {
      group.runs.sort((a, b) =>
        new Date(b.completed_at || b.session_date || b.created_at) - new Date(a.completed_at || a.session_date || a.created_at)
      );
    });

    // Filter: apply report-type filter to groups. Empty groups (no runs) are
    // also dropped on report selectors so we don't render template cards that
    // have no completed runs to pick from.
    const result = [...groups.values()].filter((group) => {
      if (reportType === 'order' && !group.isOrder) return false;
      if ((reportType === 'detail' || reportType === 'value') && group.isOrder) return false;
      if (isReportSelector && (!group.runs || group.runs.length === 0)) return false;
      return true;
    });

    // Sort by most recent run date (newest activity first); templates with no runs sort to end
    result.sort((a, b) => {
      const aDate = a.runs[0] ? new Date(a.runs[0].completed_at || a.runs[0].session_date || a.runs[0].created_at).getTime() : 0;
      const bDate = b.runs[0] ? new Date(b.runs[0].completed_at || b.runs[0].session_date || b.runs[0].created_at).getTime() : 0;
      return bDate - aDate;
    });

    return result;
  }, [countSessions, countTemplates, reportType, sessionSearch]);

  // Bucket timeline groups by the month of their latest run so the picker can
  // render collapsible month headers (current month open by default).
  const monthSections = useMemo(() => {
    const buckets = new Map();
    timelineGroups.forEach((group) => {
      const latest = group.runs[0];
      const stamp = latest?.completed_at || latest?.session_date || latest?.created_at;
      if (!stamp) return;
      const date = new Date(stamp);
      if (Number.isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!buckets.has(key)) {
        const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        buckets.set(key, { key, label, groups: [] });
      }
      buckets.get(key).groups.push(group);
    });
    // Newest month first.
    return [...buckets.values()].sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));
  }, [timelineGroups]);

  const toggleMonth = (key) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectedSessions = useMemo(
    () => selectedSessionIds.map((id) => sessionsById.get(String(id))).filter(Boolean),
    [selectedSessionIds, sessionsById]
  );

  const loadCountSessions = async () => {
    setLoadingSessions(true);
    setError('');
    try {
      const selectedStoreId = Number(localStorage.getItem('selectedStoreId'));
      if (!selectedStoreId) {
        throw new Error('Select a store before generating reports.');
      }
      setStoreId(selectedStoreId);
      setStoreName(storeNameFromLocalStorage());

      supabase
        .from('stores')
        .select('name')
        .eq('id', selectedStoreId)
        .single()
        .then(({ data: storeRow, error: storeNameError }) => {
          if (storeNameError || !storeRow?.name) return;
          setStoreName(storeRow.name);
          try {
            const stored = JSON.parse(localStorage.getItem('selectedStore') || '{}');
            localStorage.setItem('selectedStore', JSON.stringify({ ...stored, name: storeRow.name }));
            localStorage.setItem('selectedStoreName', storeRow.name);
          } catch (_) { /* no-op */ }
        })
        .catch(() => { /* ignore – localStorage value already set as fallback */ });

      const { data, error: sessionsError } = await applyActiveFilter(supabase
        .from('count_sessions')
        .select(`
          id,
          name,
          session_date,
          status,
          total_items,
          items_counted,
          counted_by,
          created_by,
          created_at,
          completed_at,
          order_calculations_enabled,
          template_id,
          template_name_snapshot,
          template_type_snapshot,
          count_group_id,
          count_commence_time,
          is_template,
          count_purpose,
          inventory_mode,
          last_accessed_at,
          score_percent,
          rabbit_count
        `)
        .eq('store_id', selectedStoreId))
        .order('completed_at', { ascending: false, nullsFirst: false });

      if (sessionsError) throw sessionsError;

      const all = data || [];

      // Compute run numbers from the full unfiltered set so numbering stays globally accurate
      const groupCounts = {};
      all.forEach((s) => {
        if (s.count_group_id) {
          groupCounts[s.count_group_id] = (groupCounts[s.count_group_id] || 0) + 1;
        }
      });
      const groupSessions = {};
      all.forEach((s) => {
        if (s.count_group_id && groupCounts[s.count_group_id] >= 2) {
          if (!groupSessions[s.count_group_id]) groupSessions[s.count_group_id] = [];
          groupSessions[s.count_group_id].push(s);
        }
      });
      const runNumberMap = {};
      Object.values(groupSessions).forEach((sessions) => {
        const sorted = [...sessions].sort((a, b) => new Date(a.session_date || a.created_at) - new Date(b.session_date || b.created_at));
        sorted.forEach((s, idx) => {
          runNumberMap[s.id] = idx + 1;
        });
      });

      // Annotate display names before filtering so _displayName is available on all sessions
      const annotatedAll = all.map((s) => {
        if (runNumberMap[s.id] != null) {
          const runDate = formatDate(s.session_date || s.created_at);
          return { ...s, _displayName: `${s.name} — Run #${runNumberMap[s.id]} · ${runDate}` };
        }
        return { ...s, _displayName: s.name };
      });

      // Separate template rows from regular sessions
      const templates = annotatedAll.filter(s => s.is_template === true);
      // Only show sessions with activity or a meaningful status — exclude zero-activity drafts
      const filtered = annotatedAll.filter(
        (session) => !session.is_template && session.status !== 'paused' && ((session.items_counted || 0) > 0 || session.status === 'in_progress' || session.status === 'completed')
      );

      setCountSessions(filtered);
      setCountTemplates(templates);

      // Build display-name map: created_by UUID → "First L." (or full name,
      // or email if no name is on file). counted_by (email) is a legacy
      // field rarely written; created_by (UUID) is what gets set today.
      const uuids = [...new Set(filtered.map((s) => s.created_by).filter(Boolean))];
      try {
        const nameMap = new Map();
        if (uuids.length) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, display_name, first_name, last_name, email')
            .in('id', uuids);
          if (profiles) {
            profiles.forEach((p) => {
              if (!p?.id) return;
              // formatUserCompact returns "First L." when both names exist,
              // first name alone, then full name, then email, then a short UUID.
              const display = formatUserCompact(p);
              if (display && display !== 'Unknown') nameMap.set(p.id, display);
            });
          }
        }
        // Seed from the cached current-user so a freshly-created session
        // by "me" still resolves even before the profile row is fetched.
        // Apply the seed regardless of whether other sessions had created_by
        // UUIDs, so a brand-new session by the current user still shows their
        // name even when every existing row pre-dates the created_by field.
        const currentUser = getCurrentUser();
        if (currentUser?.id && !nameMap.has(currentUser.id)) {
          const display = currentUser.compactName || currentUser.displayName;
          if (display && display !== 'Unknown') nameMap.set(currentUser.id, display);
        }
        // When this store isn't owned by the super admin, anything the admin
        // created here reads as "Countertop App" rather than the admin's name.
        const adminOwnsStore = await isStoreOwnedBySuperAdmin(supabase, selectedStoreId);
        if (!adminOwnsStore) {
          SUPER_ADMIN_USER_IDS.forEach((id) => nameMap.set(id, COUNTERTOP_APP_CREATOR_LABEL));
        }
        if (nameMap.size) setUserNameMap(nameMap);
        const me = getCurrentUser();
        if (me) {
          let meDisplay = me.compactName || me.displayName || me.email || '';
          // The admin generating a report in a store they don't own is shown as
          // "Countertop App" on the report's "Created By" line too.
          if (!adminOwnsStore && isSuperAdminId(me.id)) meDisplay = COUNTERTOP_APP_CREATOR_LABEL;
          if (meDisplay && meDisplay !== 'Unknown') setCurrentUserName(meDisplay);
        }
      } catch (e) { /* ignore profile lookup errors */ }
    } catch (sessionError) {
      setError(sessionError.message || 'Failed to load count sessions.');
      setCountSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const toggleSession = (sessionId) => {
    const stringId = String(sessionId);
    setSelectedSessionIds((prev) => {
      if (prev.includes(stringId)) {
        return prev.filter((id) => id !== stringId);
      }
      return [...prev, stringId];
    });
  };

  const selectAllVisible = () => {
    setSelectedSessionIds(visibleSessions.map((session) => String(session.id)));
  };

  const clearSelection = () => {
    setSelectedSessionIds([]);
  };

  const switchReportType = (nextType) => {
    setReportType(nextType);
    setSelectedSessionIds([]);
    setReportData(null);
  };

  const generateDetailReport = async (sessionIds, sessionMap) => {
    const { data, error: rowsError } = await supabase
      .from('count_items')
      .select('count_session_id, actual_quantity, location, item_snapshot, snapshot_at_close, inventory_item_id, pl_category_snapshot')
      .in('count_session_id', sessionIds);

    if (rowsError) throw rowsError;

    const rows = (data || []).map((row) => {
      const snap = reportingSnapshotFromCountItem(row);
      const session = sessionMap.get(String(row.count_session_id));
      const quantity = parseFloat(row.actual_quantity) || 0;
      const unitPrice = unitValuePriceFromCountItem(row);
      const itemValue = quantity * unitPrice;
      const valuation = describeValuation(snap);
      const subcategory = snap.category || 'Uncategorized';
      const plCategory = row.pl_category_snapshot || snap.pl_category || 'Unmapped';

      return {
        sessionId: row.count_session_id,
        sessionName: session?.name || `Session ${row.count_session_id}`,
        countDate: countDateFromSession(session),
        countedBy: session?.created_by || session?.counted_by || null,
        category: subcategory,
        subcategory,
        plCategory,
        description: snap.description || 'Unnamed Item',
        brand: snap.brand || '',
        vendor: snap.vendor || '',
        location: row.location || '-',
        quantity,
        unitPrice,
        itemValue,
        priceMissing: valuation.missing,
        priceSource: valuation.source,
        priceSourceDate: valuation.sourceDate
      };
    });

    rows.sort((a, b) => {
      const categorySort = a.category.localeCompare(b.category);
      if (categorySort !== 0) return categorySort;
      const descriptionSort = a.description.localeCompare(b.description);
      if (descriptionSort !== 0) return descriptionSort;
      return a.sessionName.localeCompare(b.sessionName);
    });

    const categoryMap = new Map();
    const sessionMapTotals = new Map();

    rows.forEach((row) => {
      categoryMap.set(row.category, (categoryMap.get(row.category) || 0) + row.itemValue);
      const currentSession = sessionMapTotals.get(row.sessionId) || {
        sessionId: row.sessionId,
        sessionName: row.sessionName,
        countDate: row.countDate,
        countedBy: row.countedBy,
        totalValue: 0,
        totalQuantity: 0
      };
      currentSession.totalValue += row.itemValue;
      currentSession.totalQuantity += row.quantity;
      sessionMapTotals.set(row.sessionId, currentSession);
    });

    const isSingleSession = sessionIds.length === 1;
    const allCountedBy = [...new Set(rows.map((r) => r.countedBy).filter(Boolean))];
    const sharedCountedBy = allCountedBy.length === 1 ? allCountedBy[0] : null;
    const allCountDates = [...new Set(rows.map((r) => r.countDate).filter(Boolean))];
    const sharedCountDate = allCountDates.length === 1 ? allCountDates[0] : null;
    const singleSessionData = isSingleSession ? sessionMap.get(String(sessionIds[0])) : null;
    const allDates = rows.map((r) => r.countDate).filter(Boolean).map((d) => new Date(d).getTime()).filter(Number.isFinite);
    const rangeStart = allDates.length ? new Date(Math.min(...allDates)).toISOString() : null;
    const rangeEnd = allDates.length ? new Date(Math.max(...allDates)).toISOString() : null;

    return {
      type: 'detail',
      isSingleSession,
      meta: {
        sessionName: isSingleSession ? (singleSessionData?.name || null) : null,
        countedBy: sharedCountedBy ? resolveCountedBy(userNameMap, sharedCountedBy) : null,
        countDate: sharedCountDate,
        rangeStart,
        rangeEnd,
        mode: 'Inventory',
      },
      rows,
      categoryData: [...categoryMap.entries()]
        .map(([category, value]) => ({ category, value }))
        .sort((a, b) => b.value - a.value),
      sessionData: [...sessionMapTotals.values()].sort(
        (a, b) => new Date(b.countDate || 0).getTime() - new Date(a.countDate || 0).getTime()
      ),
      selectedSessions: sessionIds.length,
      totalLines: rows.length,
      totalQuantity: rows.reduce((sum, row) => sum + row.quantity, 0),
      totalValue: rows.reduce((sum, row) => sum + row.itemValue, 0),
      generatedAt: new Date().toISOString()
    };
  };

  const generateValueReport = async (sessionIds, sessionMap) => {
    const { data, error: rowsError } = await supabase
      .from('count_items')
      .select('count_session_id, actual_quantity, item_snapshot, snapshot_at_close')
      .in('count_session_id', sessionIds);

    if (rowsError) throw rowsError;

    const categoryMap = new Map();
    const sessionMapTotals = new Map();
    let totalValue = 0;
    let totalQuantity = 0;

    (data || []).forEach((row) => {
      const snap = reportingSnapshotFromCountItem(row);
      const category = snap.category || 'Uncategorized';
      const quantity = parseFloat(row.actual_quantity) || 0;
      const itemValue = quantity * unitValuePriceFromCountItem(row);
      totalValue += itemValue;
      totalQuantity += quantity;
      categoryMap.set(category, (categoryMap.get(category) || 0) + itemValue);

      const session = sessionMap.get(String(row.count_session_id));
      const currentSession = sessionMapTotals.get(row.count_session_id) || {
        sessionId: row.count_session_id,
        sessionName: session?.name || `Session ${row.count_session_id}`,
        countDate: countDateFromSession(session),
        countedBy: session?.created_by || session?.counted_by || null,
        totalValue: 0
      };
      currentSession.totalValue += itemValue;
      sessionMapTotals.set(row.count_session_id, currentSession);
    });

    const categoryData = [...categoryMap.entries()]
      .map(([category, value]) => ({ category, value }))
      .sort((a, b) => b.value - a.value);

    const isSingleSession = sessionIds.length === 1;
    const sessionTotalsArr = [...sessionMapTotals.values()];
    const allValueCountedBy = [...new Set(sessionTotalsArr.map((s) => s.countedBy).filter(Boolean))];
    const allValueDates = sessionTotalsArr.map((s) => s.countDate).filter(Boolean).map((d) => new Date(d).getTime()).filter(Number.isFinite);
    const singleValueSession = isSingleSession ? sessionMap.get(String(sessionIds[0])) : null;

    return {
      type: 'value',
      isSingleSession,
      meta: {
        sessionName: isSingleSession ? (singleValueSession?.name || null) : null,
        countedBy: allValueCountedBy.length === 1 ? resolveCountedBy(userNameMap, allValueCountedBy[0]) : null,
        countDate: isSingleSession && sessionTotalsArr[0] ? sessionTotalsArr[0].countDate : null,
        rangeStart: allValueDates.length ? new Date(Math.min(...allValueDates)).toISOString() : null,
        rangeEnd: allValueDates.length ? new Date(Math.max(...allValueDates)).toISOString() : null,
        mode: 'Inventory',
      },
      categoryData,
      sessionData: [...sessionMapTotals.values()].sort((a, b) => b.totalValue - a.totalValue),
      selectedSessions: sessionIds.length,
      totalCategories: categoryData.length,
      totalQuantity,
      totalValue,
      generatedAt: new Date().toISOString()
    };
  };

  const loadVendorContacts = async (selectedStoreId) => {
    const directory = await loadVendorDirectory(supabase, selectedStoreId);
    return directory?.vendorsByKey || new Map();
  };

  /**
   * Prepared-item configs, keyed by inventory_item_id. RLS scopes the read to
   * the caller's stores; the explicit store filter keeps a report for store A
   * from ever seeing store B's yields.
   */
  const loadPreparedConfigs = async (selectedStoreId) => {
    if (!selectedStoreId) return {};
    const { data, error: cfgError } = await supabase
      .from('prepared_item_configs')
      .select('*')
      .eq('store_id', selectedStoreId);
    // A store with no prepared items is the normal case, not an error: every
    // report below simply finds nothing to prepare.
    if (cfgError) return {};
    const byItem = {};
    (data || []).forEach((row) => { byItem[String(row.inventory_item_id)] = row; });
    return byItem;
  };

  const generateOrderReport = async (sessionIds, sessionMap) => {
    const orderSessionIds = sessionIds.filter((id) => isOrderSession(sessionMap.get(String(id))));
    if (!orderSessionIds.length) {
      throw new Error('Select at least one session with Order mode enabled.');
    }

    const { data, error: itemsError } = await supabase
      .from('count_items')
      // par_normalized_quantity is the vendor-neutral PAR (20260730120000) —
      // the one a prepared item's shortage must be measured against, since its
      // batches are counted in the normalized unit.
      .select('count_session_id, actual_quantity, location, inventory_item_id, par_level, par_normalized_quantity, min_order_qty, item_snapshot, snapshot_at_close, manual_order_quantity, calculated_order_quantity')
      .in('count_session_id', orderSessionIds);
    if (itemsError) throw itemsError;

    const templateIds = [...new Set(orderSessionIds
      .map((id) => sessionMap.get(String(id))?.template_id)
      .filter(Boolean))];

    const parLookup = new Map();
    if (templateIds.length) {
      const { data: parRows, error: parError } = await supabase
        .from('count_template_items')
        .select('template_id, inventory_item_id, location, par_level')
        .in('template_id', templateIds);
      if (!parError) {
        (parRows || []).forEach((parRow) => {
          const key = `${parRow.template_id}::${parRow.inventory_item_id}::${parRow.location || ''}`;
          parLookup.set(key, parseFloat(parRow.par_level));
        });
      }
    }

    const vendorContacts = await loadVendorContacts(storeId);

    // Prepared items branch out of the vendor report entirely. Their shortage
    // is real, but it is answered by making another batch — putting them in a
    // vendor bucket would file them under "Not assigned", which reads as a
    // data problem rather than a different kind of work.
    const preparedConfigs = await loadPreparedConfigs(storeId);
    const preparedRows = [];

    const vendorBuckets = new Map();
    let totalOrderValue = 0;
    let totalOnHandValue = 0;

    (data || []).forEach((row) => {
      const session = sessionMap.get(String(row.count_session_id));
      if (!session || !isOrderSession(session)) return;

      const snap = reportingSnapshotFromCountItem(row);
      // This report does not load purchase options, so it cannot tell a
      // prepared-only item from one with a legitimate purchased alternative.
      // It routes every prepared item to Prepare, which errs the safe way: the
      // rare both-make-and-buy item shows up as prep instead of appearing on a
      // vendor order nobody can send. The allocation path
      // (purchaseAllocationService) does have the options and gets it exact.
      if (isPreparedRow({ ...row, item_snapshot: snap })) {
        preparedRows.push({
          inventory_item_id: row.inventory_item_id,
          description: snap.description || 'Unnamed Item',
          par_normalized_quantity: row.par_normalized_quantity,
          par_level: row.par_level,
          current_count: row.actual_quantity,
          is_counted: true,
        });
        return;
      }
      const rawVendorName = String(snap.vendor || snap.vendor_name_snapshot || '').trim();
      const vendorAssigned = Boolean(rawVendorName);
      const vendorName = vendorAssigned ? normalizeVendorLabel(rawVendorName) : 'Not assigned';
      const vendorBucketKey = vendorAssigned ? vendorName : '__unassigned__';
      const templateKey = `${session.template_id || 'none'}::${row.inventory_item_id}::${row.location || ''}`;
      const sessionParLevel = parLookup.get(templateKey);
      const parLevel = Number.isFinite(sessionParLevel) ? sessionParLevel : parseFloat(row.par_level);
      if (!Number.isFinite(parLevel)) return;

      const quantity = parseFloat(row.actual_quantity) || 0;
      const moq = parseFloat(row.min_order_qty ?? snap.moq);
      const freshCalculated = calculateOrderQuantity(parLevel, quantity, moq);
      const persistedCalculated = row.calculated_order_quantity !== null && row.calculated_order_quantity !== undefined
        ? parseFloat(row.calculated_order_quantity)
        : null;
      const calculatedOrderQuantity = Number.isFinite(persistedCalculated) ? persistedCalculated : freshCalculated;
      const manualOrderQty = row.manual_order_quantity !== null && row.manual_order_quantity !== undefined && row.manual_order_quantity !== ''
        ? parseFloat(row.manual_order_quantity)
        : null;
      const isManualOverride = Number.isFinite(manualOrderQty) && manualOrderQty !== null;
      const orderQuantity = isManualOverride ? manualOrderQty : calculatedOrderQuantity;
      if (orderQuantity <= 0) return;
      const shortage = Math.max(0, parLevel - quantity);
      const unitPrice = unitValuePriceFromCountItem(row);
      const orderValue = orderQuantity * unitPrice;
      const onHandValue = quantity * unitPrice;

      const matchedVendor = vendorAssigned ? resolveVendorForName(vendorName, { vendorsByKey: vendorContacts }) : null;
      const contact = buildVendorContactInfo(matchedVendor || null);
      const sendReadiness = getVendorSendReadiness(contact, { vendorAssigned });

      if (!vendorBuckets.has(vendorBucketKey)) {
        vendorBuckets.set(vendorBucketKey, {
          vendorAssigned,
          vendorId: matchedVendor?.id || null,
          vendorName,
          contact,
          contactName: contact.contactName,
          contactEmail: contact.contactEmail,
          contactPhone: contact.contactPhone,
          sendReadinessCode: sendReadiness.code,
          sendReadinessReason: sendReadiness.reason,
          canSend: sendReadiness.canSend,
          items: [],
          totalOrderValue: 0,
          totalOnHandValue: 0
        });
      }

      const bucket = vendorBuckets.get(vendorBucketKey);
      bucket.items.push({
        inventoryItemId: row.inventory_item_id || null,
        sessionId: row.count_session_id,
        sessionName: session.name || `Session ${row.count_session_id}`,
        countDate: countDateFromSession(session),
        countedBy: session.created_by || session.counted_by || null,
        description: snap.description || 'Unnamed Item',
        brand: snap.brand || '',
        location: row.location || '-',
        // The pack the item was counted against, so an order raised from this
        // report reaches the vendor saying what one unit is.
        packSize: resolvePoLinePackSize(snap),
        quantity,
        parLevel,
        moq: Number.isFinite(moq) ? moq : null,
        shortage,
        orderQuantity,
        manualOrderQty: isManualOverride ? manualOrderQty : null,
        unitPrice,
        orderValue,
        onHandValue,
        isManualOverride,
      });
    });

    // Consolidate location rows: one line per item per vendor.
    // Combines on-hand counts across all locations, uses canonical (max)
    // PAR & MOQ, and recomputes a single order quantity from the combined
    // shortage. Manual per-location overrides are summed.
    const consolidateVendorItems = (items) => {
      const groups = new Map();
      items.forEach((it) => {
        const key = it.inventoryItemId
          || `${(it.description || '').toLowerCase()}|${(it.brand || '').toLowerCase()}`;
        if (!groups.has(key)) {
          groups.set(key, {
            rows: [],
            sessionIds: new Set(),
            sessionNames: new Set(),
            countDates: [],
            countedBys: new Set(),
            locations: new Set(),
          });
        }
        const g = groups.get(key);
        g.rows.push(it);
        if (it.sessionId) g.sessionIds.add(String(it.sessionId));
        if (it.sessionName) g.sessionNames.add(it.sessionName);
        if (it.countDate) g.countDates.push(it.countDate);
        if (it.countedBy) g.countedBys.add(it.countedBy);
        if (it.location && it.location !== '-') g.locations.add(it.location);
      });
      return Array.from(groups.values()).map((g) => {
        const first = g.rows[0];
        const totalQty = g.rows.reduce((s, r) => s + (Number(r.quantity) || 0), 0);
        const canonicalPar = g.rows.reduce((m, r) => (
          Number.isFinite(r.parLevel) ? Math.max(m, r.parLevel) : m
        ), 0);
        const canonicalMoq = g.rows.reduce((m, r) => (
          Number.isFinite(r.moq) ? Math.max(m, r.moq) : m
        ), 0);
        const unitPrice = g.rows.reduce((m, r) => (
          Number.isFinite(r.unitPrice) && r.unitPrice > 0 ? Math.max(m, r.unitPrice) : m
        ), 0);
        const manualRows = g.rows.filter((r) => r.isManualOverride && Number.isFinite(r.manualOrderQty));
        const isManualOverride = manualRows.length > 0;
        const orderQuantity = isManualOverride
          ? manualRows.reduce((s, r) => s + r.manualOrderQty, 0)
          : calculateOrderQuantity(canonicalPar, totalQty, canonicalMoq);
        const shortage = Math.max(0, canonicalPar - totalQty);
        const orderValue = orderQuantity * unitPrice;
        const onHandValue = totalQty * unitPrice;
        const locationsArr = Array.from(g.locations).sort();
        const earliestDate = g.countDates.length
          ? new Date(Math.min(...g.countDates.map((d) => new Date(d).getTime()).filter(Number.isFinite))).toISOString()
          : null;
        return {
          inventoryItemId: first.inventoryItemId,
          sessionId: g.sessionIds.size === 1 ? Array.from(g.sessionIds)[0] : null,
          sessionName: g.sessionNames.size === 1 ? Array.from(g.sessionNames)[0] : Array.from(g.sessionNames).join(', '),
          countDate: earliestDate,
          countedBy: g.countedBys.size === 1 ? Array.from(g.countedBys)[0] : null,
          description: first.description,
          brand: first.brand,
          packSize: first.packSize || '',
          location: locationsArr.length > 1
            ? `${locationsArr.length} locations`
            : (locationsArr[0] || '-'),
          locations: locationsArr,
          locationCount: locationsArr.length,
          quantity: totalQty,
          parLevel: canonicalPar,
          moq: canonicalMoq || null,
          shortage,
          orderQuantity,
          unitPrice,
          orderValue,
          onHandValue,
          isManualOverride,
        };
      });
    };

    const vendorData = [...vendorBuckets.values()]
      .map((vendor) => {
        const consolidated = consolidateVendorItems(vendor.items)
          .filter((it) => it.orderQuantity > 0);
        const totalOrderValueV = consolidated.reduce((s, it) => s + it.orderValue, 0);
        const totalOnHandValueV = consolidated.reduce((s, it) => s + it.onHandValue, 0);
        return {
          ...vendor,
          items: consolidated.sort((a, b) => a.description.localeCompare(b.description)),
          totalOrderValue: totalOrderValueV,
          totalOnHandValue: totalOnHandValueV,
        };
      })
      .filter((vendor) => vendor.items.length > 0)
      .sort((a, b) => a.vendorName.localeCompare(b.vendorName));

    totalOrderValue = vendorData.reduce((s, v) => s + v.totalOrderValue, 0);
    totalOnHandValue = vendorData.reduce((s, v) => s + v.totalOnHandValue, 0);

    // "Prepare in house" — the other half of the same shortage calculation.
    const prepTasks = buildPrepList(preparedRows, preparedConfigs).map((task) => ({
      ...task,
      unitCost: preparedConfigs[String(task.inventory_item_id)]?.cost_per_normalized_unit ?? null,
    }));
    const prepGroups = groupPrepTasks(prepTasks, 'prep_area');

    const isSingleSession = orderSessionIds.length === 1;
    const orderAllItems = vendorData.flatMap((v) => v.items);
    const orderAllCountedBy = [...new Set(orderAllItems.map((i) => i.countedBy).filter(Boolean))];
    const orderAllDates = orderAllItems.map((i) => i.countDate).filter(Boolean).map((d) => new Date(d).getTime()).filter(Number.isFinite);
    const singleOrderSession = isSingleSession ? sessionMap.get(String(orderSessionIds[0])) : null;

    return {
      type: 'order',
      isSingleSession,
      meta: {
        sessionName: isSingleSession ? (singleOrderSession?.name || null) : null,
        countedBy: orderAllCountedBy.length === 1 ? resolveCountedBy(userNameMap, orderAllCountedBy[0]) : null,
        countDate: isSingleSession && orderAllDates.length ? new Date(Math.min(...orderAllDates)).toISOString() : null,
        rangeStart: orderAllDates.length ? new Date(Math.min(...orderAllDates)).toISOString() : null,
        rangeEnd: orderAllDates.length ? new Date(Math.max(...orderAllDates)).toISOString() : null,
        mode: 'Order',
      },
      vendorData,
      vendorChartData: vendorData.map((vendor) => ({ vendor: vendor.vendorName, value: vendor.totalOrderValue })),
      prepTasks,
      prepGroups,
      totalPrepItems: prepTasks.length,
      totalPrepBatches: prepTasks.reduce((sum, t) => sum + (Number(t.batches_planned) || 0), 0),
      selectedSessions: orderSessionIds.length,
      sourceSessionIds: orderSessionIds,
      storeId,
      totalVendors: vendorData.length,
      totalOnHandValue,
      totalOrderValue,
      totalOrderLines: vendorData.reduce((sum, vendor) => sum + vendor.items.length, 0),
      generatedAt: new Date().toISOString()
    };
  };

  const generateItemReport = async () => {
    const selectedStoreId = storeId || Number(localStorage.getItem('selectedStoreId'));
    if (!selectedStoreId) {
      throw new Error('Select a store before generating item report.');
    }

    const { data, error: itemsError } = await supabase
      .from('inventory_items')
      .select('*')
      .eq('store_id', selectedStoreId)
      .order('description', { ascending: true });
    if (itemsError) throw itemsError;

    const rows = data || [];
    const allColumns = [...new Set(rows.flatMap((row) => Object.keys(row || {})))].sort((a, b) => a.localeCompare(b));
    const activeRows = rows.filter((row) => row.is_active !== false);

    return {
      type: 'item',
      rows,
      columns: allColumns,
      totalItems: rows.length,
      activeItems: activeRows.length,
      inactiveItems: Math.max(0, rows.length - activeRows.length),
      totalCategories: new Set(rows.map((row) => String(row.category || '').trim()).filter(Boolean)).size,
      totalLocations: new Set(rows.map((row) => String(row.location || '').trim()).filter(Boolean)).size,
      totalVendors: new Set(rows.map((row) => String(row.vendor || row.brand || '').trim()).filter(Boolean)).size,
      generatedAt: new Date().toISOString()
    };
  };

  const generatePurchaseOrdersReport = async () => {
    const selectedStoreId = storeId || Number(localStorage.getItem('selectedStoreId'));
    if (!selectedStoreId) {
      throw new Error('Select a store before generating purchase order report.');
    }

    const { data: rows, error: poError } = await supabase
      .from('purchase_orders')
      .select(`
        id,
        po_number,
        store_id,
        vendor_id,
        vendor_name_snapshot,
        vendor_contact_snapshot,
        ordered_by_user_id,
        ordered_by_name,
        ordered_by_email,
        source_type,
        source_count_session_id,
        source_report_session_ids,
        notes,
        currency,
        subtotal,
        total,
        line_count,
        sent_from_email,
        delivery_status,
        resend_message_id,
        sent_at,
        delivered_at,
        bounced_at,
        complained_at,
        opened_at,
        clicked_at,
        approval_required,
        approval_status,
        created_at,
        updated_at
      `)
      .eq('store_id', selectedStoreId)
      .order('created_at', { ascending: false })
      .limit(400);
    if (poError) throw poError;

    const orderIds = (rows || []).map((row) => row.id);
    const itemMap = new Map();
    if (orderIds.length) {
      const { data: itemRows, error: itemError } = await supabase
        .from('purchase_order_items')
        .select(`
          purchase_order_id,
          inventory_item_id,
          description,
          brand,
          location,
          unit,
          pack_size,
          order_quantity,
          unit_price,
          line_total,
          notes,
          sort_order
        `)
        .in('purchase_order_id', orderIds)
        .order('sort_order', { ascending: true });
      if (itemError) throw itemError;
      (itemRows || []).forEach((item) => {
        const list = itemMap.get(item.purchase_order_id) || [];
        list.push({
          inventoryItemId: item.inventory_item_id,
          description: item.description,
          brand: item.brand,
          location: item.location,
          unit: item.unit,
          packSize: resolvePoLinePackSize(item),
          orderQuantity: parseFloat(item.order_quantity) || 0,
          unitPrice: parseFloat(item.unit_price) || 0,
          lineTotal: parseFloat(item.line_total) || 0,
          notes: item.notes || '',
        });
        itemMap.set(item.purchase_order_id, list);
      });
    }

    const normalizedRows = (rows || []).map((row) => ({
      id: row.id,
      purchaseOrderId: row.id,
      poNumber: row.po_number,
      vendorId: row.vendor_id,
      vendorName: row.vendor_name_snapshot || 'Unknown Vendor',
      contact: row.vendor_contact_snapshot || {},
      orderedByName: row.ordered_by_name || row.ordered_by_email || 'Unknown User',
      orderedByEmail: row.ordered_by_email || '',
      sourceType: row.source_type,
      sourceCountSessionId: row.source_count_session_id,
      sourceReportSessionIds: row.source_report_session_ids || [],
      notes: row.notes || '',
      subtotal: parseFloat(row.subtotal) || 0,
      total: parseFloat(row.total) || 0,
      lineCount: row.line_count || 0,
      sentFromEmail: row.sent_from_email,
      deliveryStatus: row.delivery_status,
      resendMessageId: row.resend_message_id,
      sentAt: row.sent_at,
      deliveredAt: row.delivered_at,
      bouncedAt: row.bounced_at,
      complainedAt: row.complained_at,
      openedAt: row.opened_at,
      clickedAt: row.clicked_at,
      approvalRequired: row.approval_required,
      approvalStatus: row.approval_status,
      noDeliveryRecord: row.delivery_status === 'blocked_no_email',
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      items: itemMap.get(row.id) || [],
    }));

    const consolidatedVendorMap = new Map();
    normalizedRows.forEach((row) => {
      const key = row.vendorId || row.vendorName;
      if (!consolidatedVendorMap.has(key)) {
        consolidatedVendorMap.set(key, {
          vendorId: row.vendorId || null,
          vendorName: row.vendorName,
          orderCount: 0,
          total: 0,
          lineCount: 0,
          items: new Map(),
        });
      }
      const bucket = consolidatedVendorMap.get(key);
      bucket.orderCount += 1;
      bucket.total += row.total;
      bucket.lineCount += row.lineCount;
      row.items.forEach((item) => {
        const itemKey = `${item.description}::${item.location || '-'}::${item.unit || ''}`;
        const existing = bucket.items.get(itemKey) || { ...item, orderQuantity: 0, lineTotal: 0 };
        existing.orderQuantity += item.orderQuantity;
        existing.lineTotal += item.lineTotal;
        bucket.items.set(itemKey, existing);
      });
    });

    return {
      type: 'purchase-orders',
      rows: normalizedRows,
      consolidatedVendors: [...consolidatedVendorMap.values()].map((vendor) => ({
        ...vendor,
        items: [...vendor.items.values()].sort((a, b) => String(a.description || '').localeCompare(String(b.description || ''))),
      })).sort((a, b) => a.vendorName.localeCompare(b.vendorName)),
      totalOrders: normalizedRows.length,
      totalValue: normalizedRows.reduce((sum, row) => sum + row.total, 0),
      generatedAt: new Date().toISOString(),
    };
  };

  const generatePriceChangeReport = async () => {
    const selectedStoreId = storeId || Number(localStorage.getItem('selectedStoreId'));
    if (!selectedStoreId) throw new Error('Select a store before generating this report.');
    if (!priceChangeStartDate || !priceChangeEndDate) throw new Error('Select a start and end date.');

    const startTs = new Date(`${priceChangeStartDate}T00:00:00`);
    const endTs = new Date(`${priceChangeEndDate}T23:59:59.999`);
    const endIso = endTs.toISOString();

    const lookbackDate = new Date(startTs);
    lookbackDate.setFullYear(lookbackDate.getFullYear() - 3);
    const lookbackIso = lookbackDate.toISOString();

    const [phRes, itemsRes, categoryMappings] = await Promise.all([
      supabase
        .from('price_history')
        .select(PRICE_CHANGE_HISTORY_SELECT)
        .eq('store_id', selectedStoreId)
        .gte('changed_at', lookbackIso)
        .lte('changed_at', endIso)
        .order('changed_at', { ascending: true })
        .limit(20000),
      supabase
        .from('inventory_items')
        .select(PRICE_CHANGE_ITEM_SELECT)
        .eq('store_id', selectedStoreId),
      loadPriceChangeCategoryMappings({ supabase, storeId: selectedStoreId }),
    ]);

    if (phRes.error) throw phRes.error;
    if (itemsRes.error) throw itemsRes.error;

    const built = buildPriceChangeReportData({
      historyRows: phRes.data || [],
      inventoryItems: itemsRes.data || [],
      categoryMappings,
      startDate: priceChangeStartDate,
      endDate: priceChangeEndDate,
    });
    const result = built.rows.filter((row) => {
      if (pcFilterCategory && row.category !== pcFilterCategory) return false;
      if (pcFilterVendor && row.vendor !== pcFilterVendor) return false;
      if (pcFilterPnlCategory && row.pnlCategory !== pcFilterPnlCategory) return false;
      if (pcFilterLocation && row.location !== pcFilterLocation) return false;
      return true;
    });
    const insights = {
      biggestItem: [...result].sort((a, b) => Math.abs(b.absChange) - Math.abs(a.absChange))[0] || null,
      biggestVendor: summarizePriceChangeGroups(result, 'vendor')[0] || null,
      biggestCategory: summarizePriceChangeGroups(result, 'category')[0] || null,
    };

    const categories = [...new Set(result.map((r) => r.category).filter(Boolean))].sort();
    const vendors = [...new Set(result.map((r) => r.vendor).filter(Boolean))].sort();
    const pnlCategories = [...new Set(result.map((r) => r.pnlCategory).filter(Boolean))].sort();
    const locations = [...new Set(result.map((r) => r.location).filter(Boolean))].sort();

    return {
      type: 'item-price-change',
      rows: result,
      insights,
      startDate: priceChangeStartDate,
      endDate: priceChangeEndDate,
      categories,
      vendors,
      pnlCategories,
      locations,
      totalItems: result.length,
      generatedAt: new Date().toISOString(),
    };
  };

  const generateTheoreticalFoodCostReport = async () => {
    const selectedStoreId = storeId || Number(localStorage.getItem('selectedStoreId'));
    if (!selectedStoreId) throw new Error('Select a store before generating this report.');
    if (!selectedPosReportId) throw new Error('Select a POS report before generating this report.');
    const data = await postMenuRecipeCosting({
      action: 'get_theoretical_food_cost',
      store_id: selectedStoreId,
      report_id: selectedPosReportId,
    });
    return {
      type: 'theoretical-food-cost',
      ...data,
      generatedAt: new Date().toISOString(),
    };
  };

  /**
   * Prep Report — the value of the store's sub-recipes and the prep they owe.
   *
   * A sub-recipe's value is a DERIVATIVE of its component ingredients, which
   * the Recipe Lab owns. Nothing here is editable and nothing here is priced
   * at the item level: every figure below is the recipe's batch cost divided
   * by its measurable yield, exactly as the count screen and the end-of-period
   * valuation see it. Changing a prepared item's cost means changing the
   * recipe, which is the only place the ingredients live.
   */
  const generatePrepReport = async (sessionIds, sessionMap) => {
    const selectedStoreId = storeId || Number(localStorage.getItem('selectedStoreId'));
    if (!selectedStoreId) throw new Error('Select a store before generating the prep report.');

    const preparedConfigs = await loadPreparedConfigs(selectedStoreId);
    const configRows = Object.values(preparedConfigs);
    const itemIds = configRows.map((c) => c.inventory_item_id).filter((id) => id != null);

    if (!itemIds.length) {
      return {
        type: 'prep',
        rows: [],
        prepGroups: [],
        totals: { items: 0, batches: 0, onHandValue: 0, prepValue: 0 },
        sourceSessionIds: sessionIds || [],
        generatedAt: new Date().toISOString(),
      };
    }

    const [{ data: itemRows }, { data: recipeRows }] = await Promise.all([
      supabase.from('inventory_items')
        .select('id, description, unit, location, par_level, item_type, source_subrecipe_id, verified_unit_cost')
        .eq('store_id', selectedStoreId).in('id', itemIds),
      supabase.from('menu_costing_recipes')
        .select('id, name, cost, batch_yield, version')
        .in('id', configRows.map((c) => c.source_subrecipe_id).filter(Boolean)),
    ]);
    const itemById = new Map((itemRows || []).map((i) => [String(i.id), i]));
    const recipeById = new Map((recipeRows || []).map((r) => [String(r.id), r]));

    // On hand comes from the selected counts when there are any, so the report
    // can be run against a specific ordering count; otherwise it reports the
    // catalog state with no on-hand claim at all.
    const onHandByItem = new Map();
    const countedSessionIds = (sessionIds || []).filter((id) => sessionMap?.get(String(id)));
    if (countedSessionIds.length) {
      const { data: countRows } = await supabase.from('count_items')
        .select('inventory_item_id, actual_quantity, par_level, par_normalized_quantity, is_counted')
        .in('count_session_id', countedSessionIds)
        .in('inventory_item_id', itemIds);
      (countRows || []).forEach((row) => {
        const key = String(row.inventory_item_id);
        const current = onHandByItem.get(key) || { onHand: 0, par: 0, counted: false };
        const qty = parseFloat(row.actual_quantity);
        if (row.is_counted || Number.isFinite(qty)) {
          current.onHand += Number.isFinite(qty) ? qty : 0;
          current.counted = true;
        }
        const par = parseFloat(row.par_normalized_quantity ?? row.par_level);
        if (Number.isFinite(par) && par > current.par) current.par = par;
        onHandByItem.set(key, current);
      });
    }

    const rows = configRows.map((config) => {
      const item = itemById.get(String(config.inventory_item_id)) || {};
      const recipe = recipeById.get(String(config.source_subrecipe_id)) || {};
      const counted = onHandByItem.get(String(config.inventory_item_id)) || null;
      const onHand = counted?.counted ? counted.onHand : null;
      const par = counted?.par || parseFloat(item.par_level) || 0;
      const unitCost = config.cost_per_normalized_unit != null
        ? Number(config.cost_per_normalized_unit) : null;

      const plan = buildPrepList(
        [{
          inventory_item_id: config.inventory_item_id,
          description: item.description,
          par_level: par,
          current_count: onHand ?? 0,
          is_counted: onHand != null,
        }],
        { [String(config.inventory_item_id)]: config },
      )[0] || null;

      return {
        inventoryItemId: config.inventory_item_id,
        sourceSubrecipeId: config.source_subrecipe_id,
        description: item.description || recipe.name || 'Unnamed prepared item',
        recipeName: recipe.name || null,
        recipeVersion: config.recipe_version ?? null,
        prepArea: config.prep_area || null,
        location: item.location || null,
        trackingMode: config.tracking_mode || null,
        activationState: config.activation_state || null,
        // The recipe's own numbers, read-only here.
        recipeYield: config.recipe_yield_quantity != null
          ? `${config.recipe_yield_quantity} ${config.recipe_yield_unit || ''}`.trim()
          : null,
        normalizedUnit: config.normalized_unit || item.unit || null,
        normalizedYield: config.normalized_yield_quantity != null
          ? Number(config.normalized_yield_quantity) : null,
        batchCost: config.batch_cost != null ? Number(config.batch_cost) : null,
        unitCost,
        costStatus: config.cost_status || null,
        onHand,
        par,
        // Ending value of the prep currently on hand: counted quantity ×
        // the derived unit cost. Null — never $0.00 — when either is unknown.
        onHandValue: onHand != null && unitCost != null ? onHand * unitCost : null,
        need: plan?.need_normalized ?? null,
        batchesPlanned: plan?.batches_planned ?? null,
        expectedOutput: plan?.expected_output_normalized ?? null,
        expectedAfter: plan?.expected_after_normalized ?? null,
        overage: plan?.expected_overage_normalized ?? null,
        prepValue: plan?.batches_planned != null && config.batch_cost != null
          ? Number(plan.batches_planned) * Number(config.batch_cost) : null,
        blockedReason: plan?.blocked_reason || config.activation_blocked_reason || null,
        yieldReviewRequired: config.yield_review_required === true,
      };
    }).sort((a, b) => a.description.localeCompare(b.description));

    const needed = rows.filter((r) => r.batchesPlanned != null && r.batchesPlanned > 0);
    return {
      type: 'prep',
      rows,
      prepGroups: groupPrepTasks(
        needed.map((r) => ({ ...r, prep_area: r.prepArea })),
        'prep_area',
      ),
      totals: {
        items: rows.length,
        needing: needed.length,
        batches: needed.reduce((s, r) => s + (r.batchesPlanned || 0), 0),
        onHandValue: rows.reduce((s, r) => s + (r.onHandValue || 0), 0),
        prepValue: needed.reduce((s, r) => s + (r.prepValue || 0), 0),
        uncosted: rows.filter((r) => r.unitCost == null).length,
      },
      sourceSessionIds: countedSessionIds,
      generatedAt: new Date().toISOString(),
    };
  };

  const generateReport = async () => {
    if (!reportType) {
      setError('Select a report type first.');
      return;
    }
    // The prep report reads the store's sub-recipes, so it works with no count
    // selected — a count only adds on-hand quantities and shortages to it.
    if (!['item', 'purchase-orders', 'item-price-change', 'theoretical-food-cost', 'prep'].includes(reportType)
      && !selectedSessionIds.length) {
      setError('Select at least one count before generating a report.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const sessionIds = selectedSessionIds.map((id) => Number(id));
      let nextReportData;
      if (reportType === 'detail') {
        nextReportData = await generateDetailReport(sessionIds, sessionsById);
      } else if (reportType === 'value') {
        nextReportData = await generateValueReport(sessionIds, sessionsById);
      } else if (reportType === 'order') {
        nextReportData = await generateOrderReport(sessionIds, sessionsById);
      } else if (reportType === 'prep') {
        nextReportData = await generatePrepReport(sessionIds, sessionsById);
      } else if (reportType === 'item') {
        nextReportData = await generateItemReport();
      } else if (reportType === 'purchase-orders') {
        nextReportData = await generatePurchaseOrdersReport();
      } else if (reportType === 'item-price-change') {
        nextReportData = await generatePriceChangeReport();
      } else if (reportType === 'theoretical-food-cost') {
        nextReportData = await generateTheoreticalFoodCostReport();
      } else {
        throw new Error('Unsupported report type.');
      }
      setReportData(nextReportData);
    } catch (reportError) {
      setError(reportError.message || 'Error generating report.');
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setResultFooterActions(null);
    switchReportType(null);
    setError('');
    if (embedded && onEmbeddedClose) onEmbeddedClose();
  };

  // Embedded auto-run: once sessions are loaded, generate the preselected
  // session report immediately — the pill click already chose the inputs.
  // cogs (P&L) is excluded: it opens the EOP wizard like the reports page.
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (!embedded || autoRanRef.current) return;
    if (loadingSessions) return;
    if (!['detail', 'value', 'order'].includes(initialReportType)) return;
    if (!selectedSessionIds.length) return;
    autoRanRef.current = true;
    generateReport();
  }, [embedded, loadingSessions]); // eslint-disable-line react-hooks/exhaustive-deps

  const sessionPickerJsx = reportType && ['detail', 'value', 'order'].includes(reportType) ? (
    <div className="card mb-3 report-config-panel">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h5 className="mb-0">Select Count Sessions ({selectedSessionIds.length} selected)</h5>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clearSelection}>Clear</button>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={selectAllVisible}>Select All</button>
          </div>
        </div>

        <div className="row g-2 align-items-end mb-3">
          <div className={isMobile ? 'col-md-8' : 'col-12'}>
            <label htmlFor="report-session-search" className="form-label">Search Sessions</label>
            <input
              id="report-session-search"
              type="text"
              className="form-control"
              placeholder="Search by name, date, or status"
              value={sessionSearch}
              onChange={(event) => setSessionSearch(event.target.value)}
            />
          </div>
          {isMobile && (
            <div className="col-md-4">
              <button
                type="button"
                className="btn btn-primary w-100"
                disabled={loading || !selectedSessionIds.length}
                onClick={generateReport}
              >
                {loading ? 'Generating...' : `Generate ${REPORT_TYPES.find((type) => type.id === reportType)?.title}`}
              </button>
            </div>
          )}
        </div>

        {reportType === 'order' && (
          <div className="alert alert-warning py-2 mb-3">
            Order report only includes sessions where order mode is enabled.
          </div>
        )}

        {error && (
          <div className="alert alert-danger py-2 mb-3">{error}</div>
        )}

        {loadingSessions ? (
          <div className="text-center text-muted py-3">Loading count sessions...</div>
        ) : (
          <SessionTimelineStack
            groups={timelineGroups}
            isMobile={isMobile}
            expandedRunsSet={expandedTimeline}
            onToggleExpandedRuns={(groupId) =>
              setExpandedTimeline((prev) => {
                const next = new Set(prev);
                if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
                return next;
              })
            }
            formatDate={formatDate}
            emptyMessage="No sessions available for this report type."
            groupedSections={monthSections.length > 0 ? monthSections : null}
            expandedSections={expandedMonths}
            onToggleSection={toggleMonth}
            renderRunRow={(session, { isNewest, index, runsToDisplay }) => {
              const sessionId = String(session.id);
              const checked = selectedSessionIds.includes(sessionId);
              const sStatusCls = session.status === 'completed' ? 'badge-status-complete'
                : session.status === 'in_progress' ? 'badge-status-inprogress'
                  : 'badge-status-draft';
              const sStatusLbl = session.status === 'completed' ? 'Complete'
                : session.status === 'in_progress' ? 'In Progress' : 'Draft';
              const countedByKey = session.created_by || session.counted_by || null;
              const resolvedCountedBy = countedByKey ? resolveCountedBy(userNameMap, countedByKey) : '';
              const countedByDisplay = (resolvedCountedBy && resolvedCountedBy !== 'Unassigned') ? resolvedCountedBy : '';

              const completionDate = session.completed_at || session.session_date || session.created_at;
              const dateLabel = formatDate(completionDate);
              const timeLabel = formatTime12h(completionDate);

              const olderRun = runsToDisplay[index + 1] || null;
              let gapLine = null;
              if (!isNewest && olderRun) {
                const gap = dayGapBetween(
                  session.completed_at || session.session_date || session.created_at,
                  olderRun.completed_at || olderRun.session_date || olderRun.created_at,
                );
                if (gap !== null) {
                  gapLine = gap === 0 ? 'No days since previous count' : `${gap} day${gap === 1 ? '' : 's'} since previous count`;
                }
              }

              const hasPersisted = session.score_percent != null;
              const scoreDisplay = hasPersisted ? `${session.score_percent}%` : '—';
              const rabbitDisplay = hasPersisted
                ? (session.rabbit_count > 0 ? '🐰'.repeat(session.rabbit_count) : '—')
                : '—';

              const bgColor = checked ? '#EDE8E3' : isNewest ? '#FEFCFA' : '#F5F0EB';
              const borderColor = checked ? '#C8BDB6' : '#E8DDCB';

              const gridCols = isMobile
                ? '20px 46px minmax(0, 1.5fr) 84px minmax(0, 1.5fr)'
                : '22px 50px minmax(0, 1.5fr) 88px minmax(0, 1.5fr) 74px';

              const topCounterCell = (
                <div style={{ textAlign: isMobile ? 'left' : 'right', minWidth: 0 }}>
                  <div style={{ fontSize: '0.58rem', color: '#A89890', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '1px' }}>Top Counter</div>
                  <div style={{ fontSize: '0.76rem', fontWeight: 600, color: hasPersisted ? '#3E2723' : '#C4B8B0' }}>{scoreDisplay}</div>
                  <div style={{ fontSize: '0.7rem', lineHeight: 1.3, color: (hasPersisted && (session.rabbit_count ?? 0) > 0) ? '#3E2723' : '#C4B8B0' }}>
                    {rabbitDisplay}
                  </div>
                </div>
              );

              return (
                <div
                  key={session.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridCols,
                    alignItems: 'start',
                    columnGap: '6px',
                    padding: '7px 6px', marginTop: '4px', borderRadius: '6px',
                    backgroundColor: bgColor,
                    border: `1px solid ${borderColor}`,
                    cursor: 'pointer', transition: 'background-color 0.1s',
                  }}
                  onClick={() => toggleSession(session.id)}
                  onMouseEnter={(e) => { if (!checked) e.currentTarget.style.backgroundColor = '#F0EBE5'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = checked ? '#EDE8E3' : isNewest ? '#FEFCFA' : '#F5F0EB'; }}
                >
                  {/* Col 1: Checkbox */}
                  <div style={{ paddingTop: '2px' }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSession(session.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: '15px', height: '15px', accentColor: '#526B7C', cursor: 'pointer', display: 'block' }}
                    />
                  </div>

                  {/* Col 2: Latest badge slot — fixed width, empty when not newest */}
                  <div>
                    {isNewest && (
                      <span style={{
                        display: 'inline-block',
                        fontSize: '0.55rem', padding: '1px 5px', borderRadius: '6px',
                        backgroundColor: '#E3F2FD', color: '#1976D2', fontWeight: 600,
                        whiteSpace: 'nowrap', lineHeight: 1.5,
                      }}>
                        Latest
                      </span>
                    )}
                  </div>

                  {/* Col 3: Date + completion time on line 1; gap-since-previous on line 2 */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: isNewest ? 600 : 500, color: '#3E2723', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {dateLabel}{timeLabel ? ` · ${timeLabel}` : ''}
                    </div>
                    {gapLine && (
                      <div style={{ fontSize: '0.66rem', color: '#A89890', marginTop: '1px' }}>{gapLine}</div>
                    )}
                  </div>

                  {/* Col 4: Status badge + optional Order chip — fixed-width track */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span className={`badge-status ${sStatusCls}`} style={{ whiteSpace: 'nowrap', alignSelf: 'start' }}>{sStatusLbl}</span>
                    {isOrderSession(session) && (
                      <span style={{
                        fontSize: '0.55rem', padding: '1px 5px', borderRadius: '6px',
                        backgroundColor: '#F0E6D2', color: '#8B6F2A', fontWeight: 600,
                        whiteSpace: 'nowrap', alignSelf: 'start',
                      }}>
                        Order
                      </span>
                    )}
                  </div>

                  {/* Col 5: "N of M items counted" + counter name below */}
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{ fontSize: '0.76rem', color: '#3E2723', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(() => {
                        const counted = session.items_counted || 0;
                        const total = session.total_items || 0;
                        const denom = total > 0 ? total : counted;
                        if (denom > 0) return `${counted} of ${denom} items counted`;
                        return '0 items counted';
                      })()}
                    </div>
                    {countedByDisplay && (
                      <div style={{ fontSize: '0.66rem', color: '#8B7B73', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{countedByDisplay}</div>
                    )}
                    {isMobile && (
                      <div style={{ marginTop: '4px' }}>{topCounterCell}</div>
                    )}
                  </div>

                  {/* Col 6 (desktop only): Top Counter */}
                  {!isMobile && topCounterCell}
                </div>
              );
            }}
          />
        )}
      </div>
    </div>
  ) : null;

  const itemConfigJsx = reportType === 'item' ? (
    <div className="card mb-3 report-config-panel">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-1">Store Item Database Report</h5>
            <p className="text-muted mb-0">Generate full store item data, then search/sort and print/download all or subset rows.</p>
          </div>
          {isMobile && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={loading}
              onClick={generateReport}
            >
              {loading ? 'Generating...' : 'Generate Item Report'}
            </button>
          )}
        </div>
        {error && (
          <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>
        )}
      </div>
    </div>
  ) : null;

  const priceChangeConfigJsx = reportType === 'item-price-change' ? (
    <div className="card mb-3 report-config-panel">
      <div className="card-body">
        <div>
          <h5 className="mb-1">Price Change Report</h5>
          <p className="text-muted mb-0">Review recorded price movements over a selected date range, then sort by item impact, vendor, or category.</p>
        </div>
        <div className="row g-2 align-items-end mt-2">
          <div className="col-md-3">
            <label className="form-label mb-1" htmlFor="pc-start-date">Start Date</label>
            <input
              id="pc-start-date"
              type="date"
              className="form-control"
              value={priceChangeStartDate}
              onChange={(e) => setPriceChangeStartDate(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1" htmlFor="pc-end-date">End Date</label>
            <input
              id="pc-end-date"
              type="date"
              className="form-control"
              value={priceChangeEndDate}
              onChange={(e) => setPriceChangeEndDate(e.target.value)}
            />
          </div>
          {isMobile && (
            <div className="col-md-3 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-primary w-100"
                disabled={loading || !priceChangeStartDate || !priceChangeEndDate}
                onClick={generateReport}
              >
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          )}
        </div>

        {pcPreFilterLoading && (
          <div className="text-muted mt-2" style={{ fontSize: '0.82rem' }}>Loading filter options...</div>
        )}

        {pcPreFilterMeta && (
          <div className="row g-2 mt-1">
            {pcPreFilterMeta.categories.length > 0 && (
              <div className="col-md-3">
                <label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Category</label>
                <select
                  className="form-select form-select-sm"
                  value={pcFilterCategory}
                  onChange={(e) => setPcFilterCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {pcPreFilterMeta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {pcPreFilterMeta.pnlCategories.length > 0 && (
              <div className="col-md-3">
                <label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>P&amp;L Category</label>
                <select
                  className="form-select form-select-sm"
                  value={pcFilterPnlCategory}
                  onChange={(e) => setPcFilterPnlCategory(e.target.value)}
                >
                  <option value="">All P&L Categories</option>
                  {pcPreFilterMeta.pnlCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {pcPreFilterMeta.vendors.length > 0 && (
              <div className="col-md-3">
                <label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Vendor</label>
                <select
                  className="form-select form-select-sm"
                  value={pcFilterVendor}
                  onChange={(e) => setPcFilterVendor(e.target.value)}
                >
                  <option value="">All Vendors</option>
                  {pcPreFilterMeta.vendors.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            )}
            {pcPreFilterMeta.locations.length > 0 && (
              <div className="col-md-3">
                <label className="form-label mb-1" style={{ fontSize: '0.82rem' }}>Location</label>
                <select
                  className="form-select form-select-sm"
                  value={pcFilterLocation}
                  onChange={(e) => setPcFilterLocation(e.target.value)}
                >
                  <option value="">All Locations</option>
                  {pcPreFilterMeta.locations.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            )}
            {(pcFilterCategory || pcFilterVendor || pcFilterPnlCategory || pcFilterLocation) && (
              <div className="col-12">
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() => { setPcFilterCategory(''); setPcFilterVendor(''); setPcFilterPnlCategory(''); setPcFilterLocation(''); }}
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>
        )}
      </div>
    </div>
  ) : null;

  const poConfigJsx = reportType === 'purchase-orders' ? (
    <div className="card mb-3 report-config-panel">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <h5 className="mb-1">Purchase Orders Report</h5>
            <p className="text-muted mb-0">Search and review sent/pending purchase orders, then resend when needed.</p>
          </div>
          {isMobile && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={loading}
              onClick={generateReport}
            >
              {loading ? 'Generating...' : 'Generate Purchase Orders Report'}
            </button>
          )}
        </div>
        {error && (
          <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>
        )}
      </div>
    </div>
  ) : null;

  const tfcConfigJsx = reportType === 'theoretical-food-cost' && !reportData ? (
    <div className="card mb-3 report-config-panel">
      <div className="card-body">
        <div>
          <h5 className="mb-1">Theoretical Food Cost Report</h5>
          <p className="text-muted mb-0">Pick an imported POS sales report to see what the items you sold should have cost, based on product mix and completed plate costs.</p>
        </div>
        {!(storeId || Number(localStorage.getItem('selectedStoreId'))) ? (
          <div className="alert alert-warning py-2 mt-3 mb-0">Select a store before generating this report.</div>
        ) : (
          <div className="row g-2 align-items-end mt-2">
            <div className={isMobile ? 'col-12' : 'col-md-6'}>
              <label className="form-label mb-1" htmlFor="tfc-pos-report">POS Report</label>
              <select
                id="tfc-pos-report"
                className="form-select"
                value={selectedPosReportId}
                onChange={(e) => setSelectedPosReportId(e.target.value)}
                disabled={posReportsLoading || !(posReports || []).length}
              >
                {posReportsLoading ? (
                  <option value="">Loading POS reports...</option>
                ) : (posReports || []).length === 0 ? (
                  <option value="">No POS reports imported yet</option>
                ) : (
                  <>
                    <option value="">Select a POS report…</option>
                    {(posReports || []).map((r) => (
                      <option key={r.id} value={String(r.id)} disabled={r.status !== 'ready'}>
                        {`${r.file_name || 'POS report'} — ${r.report_period_start || '?'} to ${r.report_period_end || '?'} (${toReadableLabel(r.status || 'unknown')})`}
                      </option>
                    ))}
                  </>
                )}
              </select>
              {!posReportsLoading && (posReports || []).length === 0 && (
                <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.82rem' }}>
                  Import a POS sales report from the End of Period wizard first.
                </p>
              )}
            </div>
            {isMobile && (
              <div className="col-md-3 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  disabled={loading || !selectedPosReportId}
                  onClick={generateReport}
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>
            )}
          </div>
        )}
        {error && (
          <div className="alert alert-danger py-2 mt-3 mb-0">{error}</div>
        )}
      </div>
    </div>
  ) : null;

  // Embedded auto-run presentation: the pill already chose the count, so the
  // session picker is redundant — show a slim generating/error state until
  // the report data lands, then the report renders on its own.
  const embeddedAutoRun = embedded && ['detail', 'value', 'order'].includes(initialReportType);
  const embeddedAutoStatusJsx = embeddedAutoRun && !reportData ? (
    <div className="text-center py-4">
      {error ? (
        <div className="alert alert-danger py-2 mb-0">{error}</div>
      ) : (
        <div className="text-muted">
          <div className="spinner-border spinner-border-sm me-2" role="status" />
          Generating report…
        </div>
      )}
    </div>
  ) : null;

  const reportModalFooterActions = (() => {
    if (!reportType || reportType === 'cogs') return null;
    if (reportData) return resultFooterActions;
    if (['detail', 'value', 'order'].includes(reportType)) {
      return (
        <button
          type="button"
          className="btn btn-primary"
          disabled={loading || !selectedSessionIds.length}
          onClick={generateReport}
        >
          {loading ? 'Generating…' : `Generate ${REPORT_TYPES.find((t) => t.id === reportType)?.title || 'Report'}`}
        </button>
      );
    }
    if (reportType === 'item') {
      return (
        <button type="button" className="btn btn-primary" disabled={loading} onClick={generateReport}>
          {loading ? 'Generating…' : 'Generate Item Report'}
        </button>
      );
    }
    if (reportType === 'item-price-change') {
      return (
        <button
          type="button"
          className="btn btn-primary"
          disabled={loading || !priceChangeStartDate || !priceChangeEndDate}
          onClick={generateReport}
        >
          {loading ? 'Generating…' : 'Generate Report'}
        </button>
      );
    }
    if (reportType === 'purchase-orders') {
      return (
        <button type="button" className="btn btn-primary" disabled={loading} onClick={generateReport}>
          {loading ? 'Generating…' : 'Generate Purchase Orders Report'}
        </button>
      );
    }
    if (reportType === 'theoretical-food-cost') {
      return (
        <button
          type="button"
          className="btn btn-primary"
          disabled={loading || !selectedPosReportId}
          onClick={generateReport}
        >
          {loading ? 'Generating…' : 'Generate Theoretical Food Cost'}
        </button>
      );
    }
    return null;
  })();

  const cogsContentJsx = reportType === 'cogs' ? (
    <>
      {showPNLSettings && (
        <div style={{ marginBottom: '1.5rem', border: '2px solid #E8DDD0', borderRadius: 12, padding: '1.5rem', background: '#FEFCFA' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h5 style={{ margin: 0, fontFamily: "'Montserrat', sans-serif", fontWeight: 700, color: '#5A4A42' }}>
              ⚙️ P&amp;L Category Mapping
            </h5>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setShowPNLWizard(true)}
                style={{ background: 'none', border: '1px solid #C8B8A8', borderRadius: 6, padding: '2px 10px', color: '#5A4A42', cursor: 'pointer', fontSize: '0.82rem', fontFamily: "'Montserrat', sans-serif" }}
              >
                🧭 Guided setup
              </button>
              <button
                type="button"
                onClick={() => setShowPNLSettings(false)}
                style={{ background: 'none', border: '1px solid #D4C4B0', borderRadius: 6, padding: '2px 10px', color: '#8B7B73', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                ✕ Close
              </button>
            </div>
          </div>
          <PNLCategoriesSettings onDone={() => setShowPNLSettings(false)} />
        </div>
      )}

      {!showPNLSettings && storeId && hasInProgressWizard(storeId) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: '#FFF8F0', border: '1px solid #E8DDD0', borderRadius: 10, padding: '0.65rem 1rem', marginBottom: '1rem', fontFamily: "'Montserrat', sans-serif" }}>
          <span style={{ fontSize: '0.8rem', color: '#5A4A42' }}>
            ⚙️ You have an unfinished P&L setup wizard.
          </span>
          <button
            type="button"
            onClick={() => setShowPNLWizard(true)}
            style={{ background: '#5A4A42', color: '#FAF6F2', border: 'none', borderRadius: 7, padding: '5px 14px', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", whiteSpace: 'nowrap' }}
          >
            Resume Setup →
          </button>
        </div>
      )}

      <EOPWizard
        key="eop-wizard"
        onNavigateToSettings={() => setShowPNLSettings(true)}
        onBack={closeModal}
        mobileOutputTitle={MOBILE_REPORT_TITLES.cogs}
      />
    </>
  ) : null;

  return (
    <div className={embedded ? 'reports-page' : 'container-fluid reports-page py-3'}>
      {!embedded && (
        <>
          <div className="row">
            <div className="col-12">
              <div className="reports-header mb-3">
                <h2 className="mb-1">Reports</h2>
                <p className="text-muted mb-0">Generate inventory detail, value, and order reports from one to all counts.</p>
              </div>
            </div>
          </div>

          <div className="report-cards-grid mb-3">
            {REPORT_TYPES.map((type) => (
              <div className="report-card-cell" key={type.id}>
                <button
                  type="button"
                  className={`report-type-card ${reportType === type.id ? 'active' : ''}`}
                  onClick={() => (type.href ? (window.location.href = type.href) : switchReportType(type.id))}
                >
                  <div className="report-type-icon">{type.icon}</div>
                  <div className="report-type-title">{type.title}</div>
                  <div className="report-type-description">{type.description}</div>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {!embedded && reportType !== 'cogs' && (reportType && ['detail', 'value', 'order'].includes(reportType) ? (
        isMobile ? (
          <MobileReportPopup
            title={MOBILE_REPORT_TITLES[reportType] || 'Report'}
            onClose={closeModal}
          >
            {sessionPickerJsx}
          </MobileReportPopup>
        ) : null
      ) : reportType === 'item' ? (
        isMobile ? itemConfigJsx : null
      ) : reportType === 'item-price-change' ? (
        isMobile ? priceChangeConfigJsx : null
      ) : reportType === 'theoretical-food-cost' ? (
        isMobile ? tfcConfigJsx : null
      ) : reportType === 'purchase-orders' ? (
        isMobile ? poConfigJsx : null
      ) : null)}

      {!embedded && isMobile && cogsContentJsx}

      {!embedded && reportData && ['detail', 'value', 'order', 'item'].includes(reportData.type) && isMobile ? (
        <MobileReportPopup
          title={MOBILE_REPORT_TITLES[reportData.type] || 'Report'}
          onClose={() => setReportData(null)}
        >
          {reportData.type === 'detail' && <DetailReportDisplay data={reportData} storeName={storeName} userNameMap={userNameMap} createdBy={currentUserName} />}
          {reportData.type === 'value' && <ValueReportDisplay data={reportData} storeName={storeName} userNameMap={userNameMap} createdBy={currentUserName} />}
          {reportData.type === 'order' && <OrderReportDisplay data={reportData} storeName={storeName} userNameMap={userNameMap} createdBy={currentUserName} />}
          {reportData.type === 'item' && <ItemReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} />}
          {reportData.type === 'prep' && <PrepReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} />}
        </MobileReportPopup>
      ) : null}
      {!embedded && isMobile && reportData?.type === 'item-price-change' && <RedesignedPriceChangeReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} />}
      {!embedded && isMobile && reportData?.type === 'purchase-orders' && <PurchaseOrdersReportDisplay data={reportData} onRefresh={generateReport} storeName={storeName} createdBy={currentUserName} />}
      {!embedded && isMobile && reportData?.type === 'theoretical-food-cost' && <TheoreticalFoodCostReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} />}

      {/* ─── Desktop (and embedded count-page frame): unified report modal.
           Embedded mode always uses the modal — ReportModal is already
           full-screen on mobile widths, so the count page gets one
           consistent frame on every device. ─── */}
      {(embedded || !isMobile) && reportType && (
        <ReportModal reportTypeId={reportType} onClose={closeModal} footerActions={reportModalFooterActions}>
          {cogsContentJsx}
          {embeddedAutoRun ? embeddedAutoStatusJsx : sessionPickerJsx}
          {itemConfigJsx}
          {priceChangeConfigJsx}
          {poConfigJsx}
          {tfcConfigJsx}
          {reportData?.type === 'detail' && <DetailReportDisplay data={reportData} storeName={storeName} userNameMap={userNameMap} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
          {reportData?.type === 'value' && <ValueReportDisplay data={reportData} storeName={storeName} userNameMap={userNameMap} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
          {reportData?.type === 'order' && <OrderReportDisplay data={reportData} storeName={storeName} userNameMap={userNameMap} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
          {reportData?.type === 'item' && <ItemReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
          {reportData?.type === 'prep' && <PrepReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
          {reportData?.type === 'item-price-change' && <RedesignedPriceChangeReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
          {reportData?.type === 'purchase-orders' && <PurchaseOrdersReportDisplay data={reportData} onRefresh={generateReport} storeName={storeName} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
          {reportData?.type === 'theoretical-food-cost' && <TheoreticalFoodCostReportDisplay data={reportData} storeName={storeName} createdBy={currentUserName} onFooterActions={setResultFooterActions} />}
        </ReportModal>
      )}

      {showPNLWizard && (
        <PNLSetupWizard
          storeId={storeId}
          onClose={() => setShowPNLWizard(false)}
          onComplete={() => setShowPNLWizard(false)}
          isRerun
        />
      )}


      <style>{`
        .reports-page { color: #4b3f37; }
        .reports-header h2 {
          font-family: 'Montserrat', sans-serif;
          font-weight: 800;
          color: #3D2518;
        }
        .report-cards-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 0.75rem;
        }
        @media (max-width: 768px) {
          .report-cards-grid {
            grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          }
        }
        .report-card-cell { display: flex; }
        .report-type-card {
          width: 100%;
          border: 1.5px solid #E9DFD6;
          border-radius: 12px;
          background: #fff;
          text-align: left;
          padding: 1rem;
          transition: all 0.15s;
          display: flex;
          flex-direction: column;
        }
        .report-type-card:hover {
          border-color: #526B7C;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(90, 74, 66, 0.1);
        }
        .report-type-card.active {
          border-color: #526B7C;
          background: #F7F9FB;
        }
        .report-type-icon {
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .report-type-title {
          font-weight: 700;
          margin-bottom: 0.25rem;
        }
        .report-type-description {
          font-size: 0.85rem;
          color: #7a6e66;
          flex: 1;
        }
        .session-timeline {
          max-height: 520px;
          overflow-y: auto;
          padding: 2px 1px;
        }
        .badge-status {
          display: inline-block;
          font-size: 0.65rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 0.15rem 0.5rem;
          border-radius: 4px;
          vertical-align: middle;
          flex-shrink: 0;
        }
        .badge-status-complete {
          background: #E8F5E9;
          color: #2E7D32;
        }
        .badge-status-inprogress {
          background: #FFF3E0;
          color: #E65100;
        }
        .badge-status-draft {
          background: #F0EBE5;
          color: #8B7B73;
        }
        .rdt-more-cell { display: none; }
        @media (max-width: 768px) {
          .rdt-more-cell { display: table-cell; }
          .rdt-wrapper {
            border: 1px solid #e8e0d8;
            border-radius: 10px;
            overflow: auto;
            max-height: 62vh;
            -webkit-overflow-scrolling: touch;
          }
          .rdt-wrapper table {
            table-layout: auto !important;
            width: auto !important;
            min-width: 100%;
          }
          .rdt-wrapper table th,
          .rdt-wrapper table td {
            font-size: 0.78rem;
            padding: 0.5rem 0.5rem;
          }
          .rdt-wrapper tbody tr {
            cursor: pointer;
            -webkit-tap-highlight-color: rgba(90,74,66,0.08);
          }
          .rdt-wrapper tbody tr:active {
            background: rgba(90,74,66,0.08) !important;
          }
          .rdt-secondary { display: none !important; }
          .rdt-primary {
            position: sticky;
            left: 0;
            z-index: 2;
            background: #fff;
            min-width: 130px;
            max-width: 180px;
          }
          .rdt-wrapper table thead .rdt-primary {
            z-index: 3;
            background: #f8f5f2;
          }
          .rdt-wrapper table tfoot .rdt-primary {
            background: #d1e7dd;
          }
          .rdt-primary::after {
            content: '';
            position: absolute;
            top: 0; right: -5px; bottom: 0;
            width: 5px;
            background: linear-gradient(to right, rgba(0,0,0,0.07), transparent);
            pointer-events: none;
          }
          .rdt-more-cell {
            color: #8b7b73;
            font-size: 0.78rem;
            text-align: center;
            letter-spacing: 0.05em;
            white-space: nowrap;
            min-width: 28px;
            width: 28px;
          }
          .rdt-wrapper table thead th {
            position: sticky;
            top: 0;
            z-index: 4;
            background: #f8f5f2;
          }
        }
        @media print {
          .report-cards-grid,
          .reports-header,
          .report-config-panel {
            display: none !important;
          }
          .rds-overlay, .rds-sheet { display: none !important; }
          .rdt-secondary { display: table-cell !important; }
          .rdt-more-cell { display: none !important; }
          .rdt-primary { position: static !important; }
          .rdt-primary::after { display: none !important; }
        }
      `}</style>
    </div>
  );
};

const buildReportSubtitle = (meta, generatedAt) => {
  if (meta) {
    if (meta.countDate) return formatWrittenDate(meta.countDate);
    if (meta.rangeStart && meta.rangeEnd) {
      const sameDay = meta.rangeStart.slice(0, 10) === meta.rangeEnd.slice(0, 10);
      if (sameDay) return formatWrittenDate(meta.rangeStart);
      return `${formatWrittenDate(meta.rangeStart)} to ${formatWrittenDate(meta.rangeEnd)}`;
    }
  }
  return formatWrittenDate(generatedAt);
};

const resolveCountedBy = (userNameMap, countedBy) => {
  if (!countedBy) return 'Unassigned';
  if (userNameMap) {
    // Try the raw value first, then the lowercased form (postgres always
    // returns UUIDs lowercase, but be defensive for any non-UUID legacy keys).
    const direct = userNameMap.get(String(countedBy));
    if (direct) return direct;
    const lower = userNameMap.get(String(countedBy).toLowerCase());
    if (lower) return lower;
  }
  // For legacy counted_by rows that stored an email or other friendly string,
  // surface that. For unresolved UUIDs (no user_profiles row found), fall back
  // to "Unassigned" rather than dumping a 36-char UUID into the cell.
  const isUuid = /^[0-9a-f-]{36}$/i.test(String(countedBy));
  return isUuid ? 'Unassigned' : countedBy;
};

const DetailReportDisplay = ({ data, storeName, userNameMap, createdBy, onFooterActions }) => {
  const { isSingleSession, meta } = data;
  const showSessionCols = !isSingleSession;
  const [hideUncounted, setHideUncounted] = useState(false);

  const displayRows = hideUncounted ? data.rows.filter((r) => r.quantity > 0) : data.rows;

  const counters = useMemo(() => {
    const names = displayRows
      .map((r) => resolveCountedBy(userNameMap, r.countedBy))
      .filter((n) => n && n !== 'Unassigned');
    return [...new Set(names)];
  }, [displayRows, userNameMap]);

  const countDates = useMemo(() => {
    const raw = [...new Set(displayRows.map((r) => r.countDate).filter(Boolean))].sort();
    return raw.map((d) => formatDate(d));
  }, [displayRows]);

  const descriptor = useMemo(() => {
    const subtitle = buildReportSubtitle(meta, data.generatedAt);
    const extraMeta = [];
    if (meta?.sessionName) extraMeta.push({ label: 'Session', value: meta.sessionName });
    if (meta?.mode) extraMeta.push({ label: 'Mode', value: meta.mode });
    if (displayRows.some((r) => r.priceMissing)) {
      extraMeta.push({ label: 'Missing Price', value: '— = no price on file; item valued at $0 in totals' });
    }
    const cols = [
      ...(showSessionCols ? [
        { key: 'sessionName', label: 'Session', align: 'text', width: '110px' },
        { key: 'countDateFmt', label: 'Count Date', align: 'text', width: '100px' },
        { key: 'countedByFmt', label: 'Counted By', align: 'text', width: '100px' },
      ] : []),
      { key: 'description', label: 'Item', align: 'text' },
      { key: 'subcategoryFmt', label: 'Subcategory', align: 'text', width: '120px' },
      { key: 'plCategoryFmt', label: 'P&L Category', align: 'text', width: '130px' },
      { key: 'vendorFmt', label: 'Vendor', align: 'text', width: '140px' },
      { key: 'location', label: 'Location', align: 'text', width: '100px' },
      { key: 'qtyFmt', label: 'Qty', align: 'number', width: '56px' },
      { key: 'unitPriceFmt', label: 'Unit Price', align: 'currency', width: '110px' },
      { key: 'itemValueFmt', label: 'Total Value', align: 'currency', width: '110px' },
    ];
    const rows = displayRows.map((row) => ({
      sessionName: row.sessionName,
      countDateFmt: formatDate(row.countDate),
      countedByFmt: resolveCountedBy(userNameMap, row.countedBy),
      description: row.description,
      subcategoryFmt: row.subcategory || row.category,
      plCategoryFmt: (row.plCategory && row.plCategory !== 'Unmapped') ? row.plCategory : '',
      vendorFmt: row.vendor || '-',
      location: row.location,
      qtyFmt: formatNumber(row.quantity),
      unitPriceFmt: row.priceMissing ? '—' : formatCurrency(row.unitPrice),
      itemValueFmt: formatCurrency(row.itemValue),
    }));
    return {
      store: { name: storeName },
      report: {
        title: 'Inventory Detail',
        subtitle,
        counters,
        countDates,
        createdBy: createdBy || '',
        generatedAt: data.generatedAt,
        meta: extraMeta,
      },
      stats: [
        { label: 'Items', value: formatNumber(data.totalLines) },
        { label: 'Total Value', value: formatCurrency(data.totalValue) },
        ...(!isSingleSession ? [{ label: 'Sessions', value: formatNumber(data.selectedSessions) }] : []),
      ],
      sections: [{ columns: cols, rows }],
    };
  }, [data, displayRows, showSessionCols, storeName, counters, createdBy, meta, userNameMap]);

  useEffect(() => {
    if (!onFooterActions) return undefined;
    onFooterActions(
      <>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => emailReportSummary(descriptor, [`Total value: ${formatCurrency(data.totalValue)}`, `Line items: ${formatNumber(data.totalLines)}`])}>Email</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadReportCsv(descriptor, `inventory_detail_${new Date().toISOString().slice(0, 10)}.csv`)}>Download CSV</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => printReportDocument(descriptor)}>Print</button>
      </>
    );
    return () => onFooterActions(null);
  }, [descriptor, onFooterActions, data.totalValue, data.totalLines]);

  return (
    <ReportDocument
      descriptor={descriptor}
      onDownload={() => downloadReportCsv(descriptor, `inventory_detail_${new Date().toISOString().slice(0, 10)}.csv`)}
      onEmail={() => emailReportSummary(descriptor, [`Total value: ${formatCurrency(data.totalValue)}`, `Line items: ${formatNumber(data.totalLines)}`])}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 8 }} className="d-none d-md-flex">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#5A4A42', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={hideUncounted}
            onChange={(e) => setHideUncounted(e.target.checked)}
            style={{ accentColor: '#8B6F5E', cursor: 'pointer' }}
          />
          Hide items with no count
        </label>
      </div>
    </ReportDocument>
  );
};

const ValueReportDisplay = ({ data, storeName, userNameMap, createdBy, onFooterActions }) => {
  const { isSingleSession, meta } = data;

  const counters = useMemo(() => {
    const ids = (data.sessionData || []).map((s) => s.countedBy).filter(Boolean);
    const names = ids.map((id) => resolveCountedBy(userNameMap, id)).filter((n) => n && n !== 'Unassigned');
    return [...new Set(names)];
  }, [data.sessionData, userNameMap]);

  const countDates = useMemo(() => {
    const raw = [...new Set((data.sessionData || []).map((s) => s.countDate).filter(Boolean))].sort();
    return raw.map((d) => formatDate(d));
  }, [data.sessionData]);

  const descriptor = useMemo(() => {
    const subtitle = buildReportSubtitle(meta, data.generatedAt);
    const extraMeta = [];
    if (meta?.sessionName) extraMeta.push({ label: 'Session', value: meta.sessionName });
    if (meta?.mode) extraMeta.push({ label: 'Mode', value: meta.mode });
    const cols = [
      { key: 'category', label: 'Category', align: 'text' },
      { key: 'totalValueFmt', label: 'Total Value', align: 'currency', width: '110px' },
      { key: 'pctFmt', label: '% of Inventory', align: 'number', width: '110px' },
    ];
    const rows = data.categoryData.map((cat) => {
      const pct = data.totalValue > 0 ? ((cat.value / data.totalValue) * 100) : 0;
      return {
        category: cat.category,
        totalValueFmt: formatCurrency(cat.value),
        pctFmt: `${pct.toFixed(1)}%`,
      };
    });
    return {
      store: { name: storeName },
      report: {
        title: 'Inventory Value',
        subtitle,
        counters,
        countDates,
        createdBy: createdBy || '',
        generatedAt: data.generatedAt,
        meta: extraMeta,
      },
      stats: [
        { label: 'Categories', value: formatNumber(data.totalCategories) },
        { label: 'Total Value', value: formatCurrency(data.totalValue) },
        ...(!isSingleSession ? [{ label: 'Sessions', value: formatNumber(data.selectedSessions) }] : []),
      ],
      sections: [{
        columns: cols,
        rows,
        totals: { category: 'Grand Total', totalValueFmt: formatCurrency(data.totalValue), pctFmt: '100.0%' },
      }],
    };
  }, [data, storeName, counters, createdBy, meta, userNameMap]);

  useEffect(() => {
    if (!onFooterActions) return undefined;
    onFooterActions(
      <>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => emailReportSummary(descriptor, [`Total value: ${formatCurrency(data.totalValue)}`])}>Email</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadReportCsv(descriptor, `inventory_value_${new Date().toISOString().slice(0, 10)}.csv`)}>Download CSV</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => printReportDocument(descriptor)}>Print</button>
      </>
    );
    return () => onFooterActions(null);
  }, [descriptor, onFooterActions, data.totalValue]);

  return (
    <ReportDocument
      descriptor={descriptor}
      onDownload={() => downloadReportCsv(descriptor, `inventory_value_${new Date().toISOString().slice(0, 10)}.csv`)}
      onEmail={() => emailReportSummary(descriptor, [`Total value: ${formatCurrency(data.totalValue)}`])}
    />
  );
};

/**
 * Prep Report — what the kitchen owes itself, and what that prep is worth.
 *
 * Two sections, in the order a chef reads them:
 *
 *   Prepare in house — the internal equivalent of a purchase order. Grouped by
 *     prep area, never by vendor, because there isn't one.
 *   Prepared inventory value — every countable sub-recipe with its batch cost,
 *     its derived cost per unit, and the value of what is on hand.
 *
 * Every cost here is READ-ONLY and derived: the value of a sub-recipe is a
 * function of its component ingredients, which the Recipe Lab owns. There is
 * deliberately no per-item price to edit — changing what the sauce costs means
 * changing what goes in it.
 */
const PREP_COST_NOTE = 'Sub-recipe costs are calculated from their ingredients in the Recipe Lab '
  + '(batch cost ÷ measurable yield) and cannot be edited here. To change a prepared item\'s cost, '
  + 'change its recipe.';

const PrepReportDisplay = ({ data, storeName, createdBy, onFooterActions }) => {
  const descriptor = useMemo(() => {
    const totals = data.totals || {};
    const rows = data.rows || [];

    const prepColumns = [
      { key: 'description', label: 'Prepared item', align: 'text' },
      { key: 'prepAreaFmt', label: 'Prep area', align: 'text', width: '120px' },
      { key: 'onHandFmt', label: 'On hand', align: 'number', width: '96px' },
      { key: 'parFmt', label: 'PAR', align: 'number', width: '88px' },
      { key: 'needFmt', label: 'Need', align: 'number', width: '88px' },
      { key: 'batchYieldFmt', label: 'Batch yield', align: 'text', width: '110px' },
      { key: 'batchesFmt', label: 'Prepare', align: 'text', width: '96px' },
      { key: 'afterFmt', label: 'Expected after', align: 'number', width: '110px' },
      { key: 'prepValueFmt', label: 'Prep cost', align: 'currency', width: '100px' },
    ];

    const qty = (value, unit) => (
      value == null || !Number.isFinite(Number(value))
        ? '—'
        : `${formatNumber(Number(value))}${unit ? ` ${unit}` : ''}`
    );

    const needing = rows.filter((r) => r.batchesPlanned != null && r.batchesPlanned > 0);
    const prepRows = needing.map((r) => ({
      description: r.description,
      prepAreaFmt: r.prepArea || '—',
      onHandFmt: qty(r.onHand, r.normalizedUnit),
      parFmt: qty(r.par, r.normalizedUnit),
      needFmt: qty(r.need, r.normalizedUnit),
      batchYieldFmt: qty(r.normalizedYield, r.normalizedUnit),
      // "Prepare 2 batches", never "Order 2 gallons".
      batchesFmt: `${formatNumber(r.batchesPlanned)} batch${r.batchesPlanned === 1 ? '' : 'es'}`,
      afterFmt: qty(r.expectedAfter, r.normalizedUnit),
      prepValueFmt: moneyOrDash(r.prepValue),
    }));

    const valueColumns = [
      { key: 'description', label: 'Prepared item', align: 'text' },
      { key: 'recipeFmt', label: 'Sub-recipe', align: 'text', width: '150px' },
      { key: 'yieldFmt', label: 'Recipe yield', align: 'text', width: '110px' },
      { key: 'normalizedYieldFmt', label: 'Normalized yield', align: 'text', width: '120px' },
      { key: 'batchCostFmt', label: 'Batch cost', align: 'currency', width: '100px' },
      { key: 'unitCostFmt', label: 'Cost per unit', align: 'currency', width: '110px' },
      { key: 'onHandFmt', label: 'On hand', align: 'number', width: '96px' },
      { key: 'onHandValueFmt', label: 'Inventory value', align: 'currency', width: '120px' },
      { key: 'statusFmt', label: 'Status', align: 'text', width: '130px' },
    ];

    const valueRows = rows.map((r) => ({
      description: r.description,
      recipeFmt: r.recipeName || '—',
      yieldFmt: r.recipeYield || '—',
      normalizedYieldFmt: qty(r.normalizedYield, r.normalizedUnit),
      batchCostFmt: moneyOrDash(r.batchCost),
      // Sub-dime per-unit costs are the norm — $0.10/fl oz rounds to nothing
      // useful at two decimals, so the precise formatter is used.
      unitCostFmt: r.unitCost == null ? '—' : `${formatMoneyPrecise(r.unitCost)}/${r.normalizedUnit || 'unit'}`,
      onHandFmt: qty(r.onHand, r.normalizedUnit),
      onHandValueFmt: moneyOrDash(r.onHandValue),
      statusFmt: r.blockedReason
        ? 'Yield needed'
        : (r.yieldReviewRequired ? 'Review yield' : (r.costStatus === 'costed' ? 'Costed' : 'Cost incomplete')),
    }));

    const alerts = [];
    if (totals.uncosted) {
      alerts.push(`${totals.uncosted} prepared item${totals.uncosted === 1 ? ' has' : 's have'} no complete cost yet — `
        + 'their recipes still have unmatched or unpriced ingredients.');
    }
    if (rows.some((r) => r.yieldReviewRequired)) {
      alerts.push('A recipe yield changed. Review the affected batch count units and PAR calculations.');
    }

    return {
      store: { name: storeName },
      report: {
        title: 'Prep Report',
        subtitle: `Generated ${formatDate(data.generatedAt)}`,
        createdBy: createdBy || '',
        generatedAt: data.generatedAt,
        meta: [{ label: 'Cost basis', value: 'Recipe Lab (ingredients ÷ yield)' }],
        alert: { variant: alerts.length ? 'warning' : 'info', text: [PREP_COST_NOTE, ...alerts].join(' ') },
        footer: { note: 'Prepared-item costs come from their sub-recipes.', url: '/menu-costing-lab.html?tab=prepared' },
      },
      stats: [
        { label: 'Prepared items', value: formatNumber(totals.items || 0) },
        { label: 'Need prep', value: formatNumber(totals.needing || 0) },
        { label: 'Batches to make', value: formatNumber(totals.batches || 0) },
        { label: 'Prep cost', value: formatCurrency(totals.prepValue || 0) },
        { label: 'Prepared inventory value', value: formatCurrency(totals.onHandValue || 0) },
      ],
      sections: [
        {
          title: 'Prepare in house',
          sectionNote: prepRows.length
            ? 'Internal production, not a vendor order.'
            : 'Nothing to prepare — every prepared item is at or above its PAR.',
          columns: prepColumns,
          rows: prepRows,
          totals: prepRows.length
            ? { description: 'Total', batchesFmt: `${formatNumber(totals.batches || 0)} batches`, prepValueFmt: formatCurrency(totals.prepValue || 0) }
            : null,
        },
        {
          title: 'Prepared inventory value',
          sectionNote: valueRows.length
            ? 'Counted quantity × cost per unit, where the cost per unit is the recipe batch cost ÷ its measurable yield.'
            : 'No sub-recipes are tracked as inventory yet. Open a sub-recipe in the Recipe Lab and choose “Make this countable”.',
          columns: valueColumns,
          rows: valueRows,
          totals: valueRows.length
            ? { description: 'Total', onHandValueFmt: formatCurrency(totals.onHandValue || 0) }
            : null,
        },
      ],
    };
  }, [data, storeName, createdBy]);

  useEffect(() => {
    if (!onFooterActions) return undefined;
    const summary = [
      `Batches to make: ${formatNumber(data.totals?.batches || 0)}`,
      `Prepared inventory value: ${formatCurrency(data.totals?.onHandValue || 0)}`,
    ];
    onFooterActions(
      <>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => emailReportSummary(descriptor, summary)}>Email</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadReportCsv(descriptor, `prep_report_${new Date().toISOString().slice(0, 10)}.csv`)}>Download CSV</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => printReportDocument(descriptor)}>Print</button>
      </>
    );
    return () => onFooterActions(null);
  }, [descriptor, onFooterActions, data.totals]);

  return (
    <ReportDocument
      descriptor={descriptor}
      onDownload={() => downloadReportCsv(descriptor, `prep_report_${new Date().toISOString().slice(0, 10)}.csv`)}
      onEmail={() => emailReportSummary(descriptor, [`Batches to make: ${formatNumber(data.totals?.batches || 0)}`])}
    />
  );
};

// Maps a get_theoretical_food_cost response (menu-recipe-costing edge
// function) into the unified ReportDriver descriptor. All figures arrive at
// full precision with nulls for absent values — everything is formatted for
// display here, once.
const buildTheoreticalFoodCostDescriptor = (data, storeName, createdBy) => {
  const posReport = data?.report || {};
  const summary = data?.summary || {};
  const items = Array.isArray(data?.items) ? data.items : [];
  const groups = Array.isArray(data?.groups) ? data.groups : [];
  const exceptions = data?.exceptions || {};

  // The driver renders a single alert banner, so the explanation, the cost
  // disclosure, and the coverage caveat are combined into one — escalating to
  // the warning variant whenever coverage is partial.
  const coverage = summary.costed_sales_coverage_pct;
  const partialCoverage = coverage != null && Number.isFinite(Number(coverage)) && Number(coverage) < 100;
  const alertParts = [THEORETICAL_FOOD_COST_EXPLANATION];
  if (data?.cost_disclosure) alertParts.push(data.cost_disclosure);
  if (partialCoverage) {
    alertParts.push(`Costed-sales coverage is ${Number(coverage).toFixed(1)}% — the theoretical percentage reflects only the completely costed portion of sales.`);
  }

  const meta = [];
  if (posReport.file_name) meta.push({ label: 'POS Report', value: posReport.file_name });
  meta.push({ label: 'Period', value: `${posReport.period_start || '—'} to ${posReport.period_end || '—'}` });
  if (posReport.status) meta.push({ label: 'POS Status', value: toReadableLabel(posReport.status) });

  const stats = [
    { label: 'Total POS Sales', value: moneyOrDash(summary.total_pos_sales) },
    { label: 'Included Costed Sales', value: moneyOrDash(summary.included_sales) },
    { label: 'Theoretical Food Cost', value: moneyOrDash(summary.theoretical_cost) },
    { label: 'Theoretical Food Cost %', value: pctOrDash(summary.theoretical_food_cost_pct) },
    { label: 'Theoretical Gross Profit', value: moneyOrDash(summary.theoretical_gross_profit) },
    { label: 'Costed-Sales Coverage %', value: pctOrDash(summary.costed_sales_coverage_pct) },
    { label: 'Unmapped Sales', value: moneyOrDash(summary.unmapped_sales) },
    { label: 'Incomplete-Recipe Sales', value: moneyOrDash(summary.incomplete_recipe_sales) },
    { label: 'Excluded Sales', value: moneyOrDash(summary.excluded_sales) },
  ];

  const itemColumns = [
    { key: 'name', label: 'Menu Item', align: 'text' },
    { key: 'menuSection', label: 'Menu Section', align: 'text', width: '100px' },
    { key: 'posCategory', label: 'POS Category', align: 'text', width: '100px' },
    { key: 'qtyFmt', label: 'Qty Sold', align: 'number', width: '64px' },
    { key: 'salesFmt', label: 'POS Sales', align: 'currency', width: '96px' },
    { key: 'mixFmt', label: 'Sales Mix %', align: 'number', width: '76px' },
    { key: 'priceFmt', label: 'Selling Price', align: 'currency', width: '92px' },
    { key: 'plateCostFmt', label: 'Plate Cost', align: 'currency', width: '88px' },
    { key: 'itemFcFmt', label: 'Item Food Cost %', align: 'number', width: '88px' },
    { key: 'theoCostFmt', label: 'Theoretical Cost', align: 'currency', width: '100px' },
    { key: 'grossFmt', label: 'Theoretical Gross Profit', align: 'currency', width: '110px' },
    { key: 'recipeStatus', label: 'Recipe Status', align: 'text', width: '88px' },
    { key: 'mappingStatus', label: 'Mapping Status', align: 'text', width: '96px' },
  ];
  const itemRows = items.map((item) => ({
    name: item.name || 'Unnamed Item',
    menuSection: item.menu_section || '—',
    posCategory: (item.pos_categories || []).join(', ') || '—',
    qtyFmt: formatNumber(toFiniteNumber(item.qty_sold)),
    salesFmt: moneyOrDash(item.sales),
    mixFmt: pctOrDash(item.sales_mix_pct),
    priceFmt: moneyOrDash(item.selling_price),
    plateCostFmt: moneyOrDash(item.plate_cost),
    itemFcFmt: pctOrDash(item.item_food_cost_pct),
    theoCostFmt: moneyOrDash(item.theoretical_cost),
    grossFmt: moneyOrDash(item.theoretical_gross_profit),
    recipeStatus: item.recipe_status ? toReadableLabel(item.recipe_status) : '—',
    mappingStatus: (item.mapping_states || []).map(toReadableLabel).join(', ') || '—',
  }));
  const sections = [{
    title: 'Item Detail',
    columns: itemColumns,
    rows: itemRows,
    totals: {
      name: 'All Items',
      qtyFmt: formatNumber(items.reduce((sum, item) => sum + toFiniteNumber(item.qty_sold), 0)),
      salesFmt: formatCurrency(items.reduce((sum, item) => sum + toFiniteNumber(item.sales), 0)),
      theoCostFmt: formatCurrency(items.reduce((sum, item) => sum + toFiniteNumber(item.theoretical_cost), 0)),
      grossFmt: formatCurrency(items.reduce((sum, item) => sum + toFiniteNumber(item.theoretical_gross_profit), 0)),
    },
  }];

  const groupColumns = [
    { key: 'name', label: 'Group', align: 'text' },
    { key: 'qtyFmt', label: 'Qty Sold', align: 'number', width: '72px' },
    { key: 'salesFmt', label: 'Included Sales', align: 'currency', width: '104px' },
    { key: 'mixFmt', label: 'Sales Mix %', align: 'number', width: '80px' },
    { key: 'theoCostFmt', label: 'Theoretical Cost', align: 'currency', width: '110px' },
    { key: 'fcFmt', label: 'Food Cost %', align: 'number', width: '84px' },
    { key: 'grossFmt', label: 'Theoretical Gross Profit', align: 'currency', width: '120px' },
    { key: 'totalSalesFmt', label: 'Total POS Sales', align: 'currency', width: '110px' },
    { key: 'coverageFmt', label: 'Costed Coverage %', align: 'number', width: '96px' },
  ];
  const buildGroupSection = (type, title) => {
    const rows = groups.filter((group) => group.type === type);
    if (!rows.length) return null;
    return {
      title,
      columns: groupColumns,
      rows: rows.map((group) => ({
        name: group.name || '—',
        qtyFmt: formatNumber(toFiniteNumber(group.qty_sold)),
        salesFmt: moneyOrDash(group.sales),
        mixFmt: pctOrDash(group.sales_mix_pct),
        theoCostFmt: moneyOrDash(group.theoretical_cost),
        fcFmt: pctOrDash(group.food_cost_pct),
        grossFmt: moneyOrDash(group.theoretical_gross_profit),
        totalSalesFmt: moneyOrDash(group.total_sales),
        coverageFmt: pctOrDash(group.costed_sales_coverage_pct),
      })),
    };
  };
  const menuSectionSection = buildGroupSection('menu_section', 'By Menu Section');
  const posCategorySection = buildGroupSection('pos_category', 'By POS Category');
  if (menuSectionSection) sections.push(menuSectionSection);
  if (posCategorySection) sections.push(posCategorySection);

  // Exceptions: one section per non-empty bucket, omitted entirely when the
  // POS report costed cleanly.
  const posItemLabel = (row) => `${row.pos_name || 'POS item'}${row.size_modifier ? ` (${row.size_modifier})` : ''}`;
  const excColumns = [
    { key: 'posItem', label: 'POS Item', align: 'text' },
    { key: 'category', label: 'Category', align: 'text', width: '110px' },
    { key: 'qtyFmt', label: 'Qty', align: 'number', width: '60px' },
    { key: 'salesFmt', label: 'Sales', align: 'currency', width: '96px' },
  ];
  const excBaseRow = (row) => ({
    posItem: posItemLabel(row),
    category: row.category || '—',
    qtyFmt: formatNumber(toFiniteNumber(row.qty_sold)),
    salesFmt: moneyOrDash(row.sales_amount),
  });
  const confidenceText = (value) => {
    if (value == null) return '—';
    if (Number.isFinite(Number(value))) {
      const n = Number(value);
      return `${Math.round(n <= 1 ? n * 100 : n)}%`;
    }
    return toReadableLabel(value);
  };
  const missingSummary = (row) => {
    const parts = [];
    const ingredients = (row.missing_ingredients || []).length;
    const prices = (row.missing_prices || []).length;
    const conversions = (row.missing_conversions || []).length;
    if (ingredients) parts.push(`${ingredients} ingredient${ingredients === 1 ? '' : 's'}`);
    if (prices) parts.push(`${prices} price${prices === 1 ? '' : 's'}`);
    if (conversions) parts.push(`${conversions} conversion${conversions === 1 ? '' : 's'}`);
    const uncosted = toFiniteNumber(row.uncosted_lines);
    if (uncosted > 0) parts.push(`${uncosted} uncosted line${uncosted === 1 ? '' : 's'}`);
    if (parts.length) return `Missing ${parts.join(', ')}`;
    return row.recipe_status ? toReadableLabel(row.recipe_status) : '—';
  };

  const exceptionSections = [];
  const unmappedRows = Array.isArray(exceptions.unmapped_rows) ? exceptions.unmapped_rows : [];
  if (unmappedRows.length) {
    exceptionSections.push({
      title: 'Unmapped POS Rows',
      sectionNote: 'These POS lines have no menu item mapping, so their sales are excluded from the theoretical cost.',
      columns: [
        ...excColumns,
        { key: 'status', label: 'Status', align: 'text', width: '96px' },
        { key: 'reason', label: 'Reason', align: 'text' },
      ],
      rows: unmappedRows.map((row) => ({
        ...excBaseRow(row),
        status: row.mapping_state ? toReadableLabel(row.mapping_state) : '—',
        reason: row.reason || '—',
      })),
    });
  }
  const unconfirmedMappings = Array.isArray(exceptions.unconfirmed_mappings) ? exceptions.unconfirmed_mappings : [];
  if (unconfirmedMappings.length) {
    exceptionSections.push({
      title: 'Unconfirmed Mappings',
      sectionNote: 'Suggested menu item matches that still need review — their sales are excluded until confirmed.',
      columns: [
        ...excColumns,
        { key: 'suggested', label: 'Suggested Item', align: 'text' },
        { key: 'confidence', label: 'Confidence', align: 'number', width: '88px' },
      ],
      rows: unconfirmedMappings.map((row) => ({
        ...excBaseRow(row),
        suggested: row.suggested_name || row.menu_item_name || '—',
        confidence: confidenceText(row.confidence),
      })),
    });
  }
  const incompleteRecipes = Array.isArray(exceptions.incomplete_recipes) ? exceptions.incomplete_recipes : [];
  if (incompleteRecipes.length) {
    exceptionSections.push({
      title: 'Incomplete Recipes',
      sectionNote: 'These items sold but could not be fully costed — finish their recipes in Menu Costing Lab.',
      columns: [
        ...excColumns,
        { key: 'menuItem', label: 'Menu Item', align: 'text' },
        { key: 'missing', label: 'Missing', align: 'text' },
      ],
      rows: incompleteRecipes.map((row) => ({
        ...excBaseRow(row),
        menuItem: row.menu_item_name || '—',
        missing: missingSummary(row),
      })),
    });
  }
  const missingPrices = Array.isArray(exceptions.missing_prices) ? exceptions.missing_prices : [];
  if (missingPrices.length) {
    exceptionSections.push({
      title: 'Missing Selling Prices',
      sectionNote: 'No selling price on file, so food cost % cannot be computed for these items.',
      columns: [
        ...excColumns,
        { key: 'menuItem', label: 'Menu Item', align: 'text' },
      ],
      rows: missingPrices.map((row) => ({
        ...excBaseRow(row),
        menuItem: row.menu_item_name || '—',
      })),
    });
  }
  const missingConversions = Array.isArray(exceptions.missing_conversions) ? exceptions.missing_conversions : [];
  if (missingConversions.length) {
    exceptionSections.push({
      title: 'Missing Unit Conversions',
      sectionNote: 'A purchase-to-recipe unit conversion is missing for these items.',
      columns: [
        ...excColumns,
        { key: 'menuItem', label: 'Menu Item', align: 'text' },
      ],
      rows: missingConversions.map((row) => ({
        ...excBaseRow(row),
        menuItem: row.menu_item_name || '—',
      })),
    });
  }
  const excludedItems = Array.isArray(exceptions.excluded_items) ? exceptions.excluded_items : [];
  if (excludedItems.length) {
    exceptionSections.push({
      title: 'Excluded Items',
      sectionNote: 'These POS lines were excluded from the theoretical food cost.',
      columns: [
        ...excColumns,
        { key: 'menuItem', label: 'Menu Item', align: 'text' },
        { key: 'reason', label: 'Reason', align: 'text' },
      ],
      rows: excludedItems.map((row) => ({
        ...excBaseRow(row),
        menuItem: row.menu_item_name || '—',
        reason: row.reason || '—',
      })),
    });
  }
  const dataWarnings = Array.isArray(exceptions.data_warnings) ? exceptions.data_warnings : [];
  if (dataWarnings.length) {
    exceptionSections.push({
      title: 'Data Warnings',
      sectionNote: 'Issues spotted in the POS data that may affect this report.',
      columns: [
        ...excColumns,
        { key: 'reason', label: 'Reason', align: 'text' },
      ],
      rows: dataWarnings.map((row) => ({
        ...excBaseRow(row),
        reason: row.reason || '—',
      })),
    });
  }
  // The driver renders report.footer.url as plain text (not a link), so the
  // Menu Costing Lab pointer is repeated on the first exceptions section —
  // the place a user lands when something needs fixing.
  if (exceptionSections.length) {
    exceptionSections[0].sectionNote = `${exceptionSections[0].sectionNote} Review mappings and recipe costs in Menu Costing Lab (/menu-costing-lab.html).`;
  }
  sections.push(...exceptionSections);

  return {
    store: { name: storeName },
    report: {
      title: 'Theoretical Food Cost Report',
      subtitle: `${posReport.file_name || 'POS report'} — ${formatDate(posReport.period_start)} to ${formatDate(posReport.period_end)}`,
      createdBy: createdBy || '',
      generatedAt: data?.generatedAt,
      alert: { variant: partialCoverage ? 'warning' : 'info', text: alertParts.join(' ') },
      meta,
      footer: { note: 'Review mappings and recipe costs in Menu Costing Lab.', url: '/menu-costing-lab.html' },
    },
    stats,
    sections,
  };
};

const TheoreticalFoodCostReportDisplay = ({ data, storeName, createdBy, onFooterActions }) => {
  const descriptor = useMemo(
    () => buildTheoreticalFoodCostDescriptor(data, storeName, createdBy),
    [data, storeName, createdBy],
  );

  useEffect(() => {
    if (!onFooterActions) return undefined;
    onFooterActions(
      <>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => emailReportSummary(descriptor, [`Theoretical food cost: ${descriptor.stats[3]?.value || '—'}`, `Theoretical cost: ${descriptor.stats[2]?.value || '—'}`])}>Email</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadReportCsv(descriptor, `theoretical_food_cost_${new Date().toISOString().slice(0, 10)}.csv`)}>Download CSV</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => printReportDocument(descriptor)}>Print</button>
      </>
    );
    return () => onFooterActions(null);
  }, [descriptor, onFooterActions]);

  return (
    <ReportDocument
      descriptor={descriptor}
      onDownload={() => downloadReportCsv(descriptor, `theoretical_food_cost_${new Date().toISOString().slice(0, 10)}.csv`)}
    />
  );
};

const OrderReportDisplay = ({ data, storeName, userNameMap, createdBy, onFooterActions }) => {
  const [showSendSheet, setShowSendSheet] = useState(false);
  const [sendSummary, setSendSummary] = useState('');

  const formatCountLabel = (count, singular, plural) => `${count} ${count === 1 ? singular : plural}`;

  const formatVendorSendSummary = (summary = {}) => {
    const pieces = [];
    if (summary.sentByEmail > 0) pieces.push(`${formatCountLabel(summary.sentByEmail, 'vendor order', 'vendor orders')} sent by email`);
    if (summary.pendingApproval > 0) pieces.push(`${formatCountLabel(summary.pendingApproval, 'vendor order', 'vendor orders')} pending approval`);
    if (summary.sentOutsideCountertop > 0) pieces.push(`${formatCountLabel(summary.sentOutsideCountertop, 'vendor order', 'vendor orders')} marked as sent outside Countertop`);
    if (summary.notSentNoVendor > 0) pieces.push(`${formatCountLabel(summary.notSentNoVendor, 'item', 'items')} not sent \u2014 no vendor assigned`);
    if (summary.notSentMissingEmail > 0) pieces.push(`${formatCountLabel(summary.notSentMissingEmail, 'item', 'items')} not sent \u2014 vendor has no contact email`);
    if (summary.errored > 0) pieces.push(`${formatCountLabel(summary.errored, 'vendor order', 'vendor orders')} failed`);
    return pieces.length ? pieces.join(', ') : 'No vendor sends were completed.';
  };

  const { isSingleSession, meta } = data;
  const showSessionCols = !isSingleSession;

  const counters = useMemo(() => {
    const ids = data.vendorData.flatMap((v) => v.items.map((i) => i.countedBy)).filter(Boolean);
    const names = ids.map((id) => resolveCountedBy(userNameMap, id)).filter((n) => n && n !== 'Unassigned');
    return [...new Set(names)];
  }, [data.vendorData, userNameMap]);

  const countDates = useMemo(() => {
    const raw = [...new Set(
      data.vendorData.flatMap((v) => v.items.map((i) => i.countDate)).filter(Boolean)
    )].sort();
    return raw.map((d) => formatDate(d));
  }, [data.vendorData]);

  const descriptor = useMemo(() => {
    const subtitle = buildReportSubtitle(meta, data.generatedAt);
    const extraMeta = [];
    if (meta?.sessionName) extraMeta.push({ label: 'Session', value: meta.sessionName });
    const sessionCols = showSessionCols ? [
      { key: 'sessionNameFmt', label: 'Session', align: 'text', width: '100px' },
      { key: 'countDateFmt', label: 'Count Date', align: 'text', width: '90px' },
      { key: 'countedByFmt', label: 'Counted By', align: 'text', width: '90px' },
    ] : [];
    const cols = [
      ...sessionCols,
      { key: 'description', label: 'Item', align: 'text' },
      { key: 'onHandFmt', label: 'On Hand', align: 'number', width: '56px' },
      { key: 'parLevelFmt', label: 'PAR', align: 'number', width: '56px' },
      { key: 'orderQtyFmt', label: 'Order Qty', align: 'number', width: '70px' },
      { key: 'unitPriceFmt', label: 'Unit Price', align: 'currency', width: '110px' },
      { key: 'orderValueFmt', label: 'Order Value', align: 'currency', width: '110px' },
    ];
    const sections = data.vendorData.map((vendor) => ({
      title: vendor.vendorName,
      sectionNote: vendor.contactEmail ? `Contact: ${vendor.contactName || ''} \u2014 ${vendor.contactEmail}` : undefined,
      columns: cols,
      rows: vendor.items.map((item) => ({
        sessionNameFmt: item.sessionName,
        countDateFmt: formatDate(item.countDate),
        countedByFmt: resolveCountedBy(userNameMap, item.countedBy),
        description: item.description,
        onHandFmt: formatNumber(item.quantity),
        parLevelFmt: formatNumber(item.parLevel),
        orderQtyFmt: `${formatNumber(item.orderQuantity)}${item.isManualOverride ? ' (M)' : ''}`,
        unitPriceFmt: formatCurrency(item.unitPrice),
        orderValueFmt: formatCurrency(item.orderValue),
      })),
      totals: {
        description: 'Vendor Total',
        orderValueFmt: formatCurrency(vendor.totalOrderValue),
      },
    }));
    return {
      store: { name: storeName },
      report: {
        title: 'Order Report',
        subtitle,
        counters,
        countDates,
        createdBy: createdBy || '',
        generatedAt: data.generatedAt,
        meta: extraMeta,
      },
      stats: [
        ...(!isSingleSession ? [{ label: 'Sessions', value: formatNumber(data.selectedSessions) }] : []),
        { label: 'Vendors', value: formatNumber(data.totalVendors) },
        { label: 'Order Lines', value: formatNumber(data.totalOrderLines) },
        { label: 'Order Value', value: formatCurrency(data.totalOrderValue) },
      ],
      sections,
    };
  }, [data, storeName, counters, createdBy, meta, showSessionCols, userNameMap, isSingleSession]);

  useEffect(() => {
    if (!onFooterActions) return undefined;
    onFooterActions(
      <>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => emailReportSummary(descriptor, [`Order value: ${formatCurrency(data.totalOrderValue)}`, `Order lines: ${formatNumber(data.totalOrderLines)}`])}>Email</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadReportCsv(descriptor, `order_report_${new Date().toISOString().slice(0, 10)}.csv`)}>Download CSV</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => printReportDocument(descriptor)}>Print</button>
      </>
    );
    return () => onFooterActions(null);
  }, [descriptor, onFooterActions, data.totalOrderValue, data.totalOrderLines]);

  return (
    <ReportDocument
      descriptor={descriptor}
      onDownload={() => downloadReportCsv(descriptor, `order_report_${new Date().toISOString().slice(0, 10)}.csv`)}
      onEmail={() => emailReportSummary(descriptor, [`Order value: ${formatCurrency(data.totalOrderValue)}`, `Order lines: ${formatNumber(data.totalOrderLines)}`])}
    >
      <div className="alert alert-info d-flex justify-content-between flex-wrap gap-2">
        <span><strong>Total On-Hand Value:</strong> {formatCurrency(data.totalOnHandValue)}</span>
        <span><strong>Total Order Value:</strong> {formatCurrency(data.totalOrderValue)}</span>
      </div>

      {sendSummary ? <div className="alert alert-secondary py-2">{sendSummary}</div> : null}

      <div className="d-flex justify-content-end mb-3">
        <button
          type="button"
          className="btn btn-primary"
          disabled={data.vendorData.length === 0}
          onClick={() => setShowSendSheet(true)}
        >
          Send to Vendors
        </button>
      </div>

      {data.vendorData.length === 0 ? (
        <div className="text-muted">No order quantities found across selected order-enabled sessions.</div>
      ) : null}

      <VendorOrderSendSheet
        isOpen={showSendSheet}
        title="Send to Vendors"
        subtitle="Preview each vendor-specific order before sending."
        vendors={data.vendorData}
        sourceMetadata={{
          sourceType: 'report_preview',
          reportGeneratedAt: data.generatedAt,
          reportSessionIds: data.sourceSessionIds || [],
          storeId: data.storeId || null,
        }}
        onClose={() => setShowSendSheet(false)}
        onAfterSend={(summary) => { setSendSummary(formatVendorSendSummary(summary)); }}
      />
    </ReportDocument>
  );
};

const ItemReportDisplay = ({ data, storeName, createdBy, onFooterActions }) => {
  const [globalSearch, setGlobalSearch] = useState('');
  const [fieldSearchKey, setFieldSearchKey] = useState('');
  const [fieldSearchValue, setFieldSearchValue] = useState('');
  const [sortBy, setSortBy] = useState(() => (data.columns.includes('description') ? 'description' : data.columns[0] || ''));
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeItemRow, setActiveItemRow] = useState(null);

  const primaryCol = data.columns[0] || null;

  const sortedAndFilteredRows = useMemo(() => {
    const normalizedGlobalSearch = globalSearch.trim().toLowerCase();
    const normalizedFieldSearch = fieldSearchValue.trim().toLowerCase();
    const filtered = data.rows.filter((row) => {
      const globalMatch = !normalizedGlobalSearch
        || data.columns.some((column) => String(formatFieldValue(row[column], column)).toLowerCase().includes(normalizedGlobalSearch));
      if (!globalMatch) return false;
      if (!fieldSearchKey || !normalizedFieldSearch) return true;
      return String(formatFieldValue(row[fieldSearchKey], fieldSearchKey)).toLowerCase().includes(normalizedFieldSearch);
    });
    const sorted = [...filtered];
    if (sortBy) {
      sorted.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortDirection === 'asc' ? -1 : 1;
        if (bValue == null) return sortDirection === 'asc' ? 1 : -1;
        const aNumber = Number(aValue);
        const bNumber = Number(bValue);
        const bothNumeric = !Number.isNaN(aNumber) && !Number.isNaN(bNumber);
        if (bothNumeric) return sortDirection === 'asc' ? aNumber - bNumber : bNumber - aNumber;
        const aText = String(aValue).toLowerCase();
        const bText = String(bValue).toLowerCase();
        return sortDirection === 'asc' ? aText.localeCompare(bText) : bText.localeCompare(aText);
      });
    }
    return sorted;
  }, [data.columns, data.rows, fieldSearchKey, fieldSearchValue, globalSearch, sortBy, sortDirection]);

  const descriptor = useMemo(() => {
    const cols = data.columns.map((col) => ({ key: col, label: toReadableLabel(col), align: 'text' }));
    const rows = sortedAndFilteredRows.map((row) => {
      const mapped = {};
      data.columns.forEach((col) => { mapped[col] = String(formatFieldValue(row[col], col)); });
      return mapped;
    });
    return {
      store: { name: storeName },
      report: {
        title: 'Item Report',
        subtitle: formatWrittenDate(data.generatedAt),
        counters: [],
        createdBy: createdBy || '',
        generatedAt: data.generatedAt,
        meta: [{ label: 'Mode', value: 'Item Database' }],
      },
      stats: [
        { label: 'All Items', value: formatNumber(data.totalItems) },
        { label: 'Active Items', value: formatNumber(data.activeItems) },
        { label: 'Categories', value: formatNumber(data.totalCategories) },
        { label: 'Vendors', value: formatNumber(data.totalVendors) },
      ],
      sections: [{ columns: cols, rows }],
    };
  }, [data, sortedAndFilteredRows, storeName, createdBy]);

  useEffect(() => {
    if (!onFooterActions) return undefined;
    onFooterActions(
      <>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => emailReportSummary(descriptor, [`Showing ${formatNumber(sortedAndFilteredRows.length)} of ${formatNumber(data.totalItems)} items.`])}>Email</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadReportCsv(descriptor, `item_report_${new Date().toISOString().slice(0, 10)}.csv`)}>Download CSV</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => printReportDocument(descriptor)}>Print</button>
      </>
    );
    return () => onFooterActions(null);
  }, [descriptor, onFooterActions, sortedAndFilteredRows.length, data.totalItems]);

  return (
    <ReportDocument
      descriptor={descriptor}
      onDownload={() => downloadReportCsv(descriptor, `item_report_${new Date().toISOString().slice(0, 10)}.csv`)}
      onEmail={() => emailReportSummary(descriptor, [`Showing ${formatNumber(sortedAndFilteredRows.length)} of ${formatNumber(data.totalItems)} items.`])}
    >
      <div className="row g-2 align-items-end mb-3 mt-1 d-none d-md-flex">
        <div className="col-lg-4">
          <label className="form-label">Search all variables</label>
          <input
            type="text"
            className="form-control"
            value={globalSearch}
            onChange={(event) => setGlobalSearch(event.target.value)}
            placeholder="Search any column value"
          />
        </div>
        <div className="col-lg-3">
          <label className="form-label">Search specific variable</label>
          <select
            className="form-select"
            value={fieldSearchKey}
            onChange={(event) => setFieldSearchKey(event.target.value)}
          >
            <option value="">All variables</option>
            {data.columns.map((column) => (
              <option key={column} value={column}>{toReadableLabel(column)}</option>
            ))}
          </select>
        </div>
        <div className="col-lg-3">
          <label className="form-label">Variable search value</label>
          <input
            type="text"
            className="form-control"
            value={fieldSearchValue}
            onChange={(event) => setFieldSearchValue(event.target.value)}
            placeholder="Filter value"
          />
        </div>
        <div className="col-lg-2">
          <label className="form-label">Sort direction</label>
          <select
            className="form-select"
            value={sortDirection}
            onChange={(event) => setSortDirection(event.target.value)}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
      </div>

      <div className="row g-2 align-items-end mb-3 d-none d-md-flex">
        <div className="col-lg-6">
          <label className="form-label">Sort by variable</label>
          <select
            className="form-select"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            {data.columns.map((column) => (
              <option key={column} value={column}>{toReadableLabel(column)}</option>
            ))}
          </select>
        </div>
        <div className="col-lg-6" />
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2 d-none d-md-flex">
        <small className="text-muted">Showing {formatNumber(sortedAndFilteredRows.length)} of {formatNumber(data.totalItems)} items</small>
        <small className="text-muted">{formatNumber(data.columns.length)} variables available</small>
      </div>

      {activeItemRow && (
        <RowDetailsSheet
          title={activeItemRow[primaryCol] ? String(activeItemRow[primaryCol]) : 'Item Details'}
          fields={data.columns.map((col) => ({ label: toReadableLabel(col), value: String(formatFieldValue(activeItemRow[col], col)), wide: col === primaryCol }))}
          onClose={() => setActiveItemRow(null)}
        />
      )}
    </ReportDocument>
  );
};

const PurchaseOrdersReportDisplay = ({ data, onRefresh, storeName, createdBy, onFooterActions }) => {
  const [vendorSearch, setVendorSearch] = useState('');
  const [orderedBySearch, setOrderedBySearch] = useState('');
  const [activePoRow, setActivePoRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState('created_desc');
  const [showConsolidated, setShowConsolidated] = useState(false);
  const [selectedPo, setSelectedPo] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [resendingId, setResendingId] = useState(null);
  const [expandedPoId, setExpandedPoId] = useState(null);
  const [copiedPoId, setCopiedPoId] = useState(null);
  const isMobile = useIsMobile();

  const filteredRows = useMemo(() => {
    const filtered = (data.rows || []).filter((row) => {
      if (vendorSearch.trim() && !String(row.vendorName || '').toLowerCase().includes(vendorSearch.trim().toLowerCase())) return false;
      if (orderedBySearch.trim()) {
        const searchableUser = `${row.orderedByName || ''} ${row.orderedByEmail || ''}`.toLowerCase();
        if (!searchableUser.includes(orderedBySearch.trim().toLowerCase())) return false;
      }
      if (statusFilter && getDeliveryStatusLabel(row.deliveryStatus) !== statusFilter) return false;
      if (fromDate && new Date(row.createdAt).getTime() < new Date(`${fromDate}T00:00:00`).getTime()) return false;
      if (toDate && new Date(row.createdAt).getTime() > new Date(`${toDate}T23:59:59`).getTime()) return false;
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sortBy === 'total_desc') return b.total - a.total;
      if (sortBy === 'total_asc') return a.total - b.total;
      if (sortBy === 'vendor_asc') return String(a.vendorName || '').localeCompare(String(b.vendorName || ''));
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return sorted;
  }, [data.rows, fromDate, orderedBySearch, sortBy, statusFilter, toDate, vendorSearch]);

  const deliveryStatuses = useMemo(
    () => [...new Set((data.rows || []).map((row) => getDeliveryStatusLabel(row.deliveryStatus)).filter(Boolean))].sort(),
    [data.rows]
  );

  const selectedPreview = useMemo(() => {
    if (!selectedPo) return null;
    const purchaseOrder = {
      purchaseOrderId: selectedPo.id,
      poNumber: selectedPo.poNumber,
      vendorId: selectedPo.vendorId,
      vendorName: selectedPo.vendorName,
      contact: selectedPo.contact || {},
      store: {
        storeId: selectedPo.storeId || null,
        storeName: selectedPo.storeName || '',
        locationName: '',
      },
      orderedBy: {
        name: selectedPo.orderedByName,
        email: selectedPo.orderedByEmail,
      },
      items: selectedPo.items || [],
      totals: {
        subtotal: selectedPo.subtotal || selectedPo.total || 0,
        total: selectedPo.total || 0,
      },
      metadata: {
        sourceSummary: selectedPo.sourceType || 'purchase_order',
      },
    };
    return {
      purchaseOrder,
      emailPreview: previewPurchaseOrder(purchaseOrder),
    };
  }, [selectedPo]);

  const handleResend = async (purchaseOrderId) => {
    setFeedbackMessage('');
    setResendingId(purchaseOrderId);
    try {
      const result = await resendPurchaseOrder(purchaseOrderId);
      setFeedbackMessage(result?.message || `Purchase order ${purchaseOrderId} resent.`);
      if (typeof onRefresh === 'function') {
        await onRefresh();
      }
    } catch (error) {
      setFeedbackMessage(error?.message || 'Unable to resend purchase order.');
    } finally {
      setResendingId(null);
    }
  };

  const poDescriptor = useMemo(() => {
    if (showConsolidated) {
      const cols = [
        { key: 'vendorName', label: 'Vendor', align: 'text' },
        { key: 'orderCountFmt', label: 'POs', align: 'number', width: '60px' },
        { key: 'lineCountFmt', label: 'Line Items', align: 'number', width: '80px' },
        { key: 'totalFmt', label: 'Total Value', align: 'currency', width: '110px' },
      ];
      const rows = (data.consolidatedVendors || []).map((v) => ({
        vendorName: v.vendorName,
        orderCountFmt: formatNumber(v.orderCount),
        lineCountFmt: formatNumber(v.lineCount),
        totalFmt: formatCurrency(v.total),
      }));
      return {
        store: { name: storeName },
        report: {
          title: 'Purchase Orders Report',
          subtitle: `${formatWrittenDate(data.generatedAt)} — Consolidated by Vendor`,
          counters: [],
          createdBy: createdBy || '',
          generatedAt: data.generatedAt,
          meta: [{ label: 'Vendors', value: formatNumber((data.consolidatedVendors || []).length) }],
        },
        stats: [
          { label: 'Vendors', value: formatNumber((data.consolidatedVendors || []).length) },
          { label: 'Total Value', value: formatCurrency(data.totalValue) },
        ],
        sections: [{ columns: cols, rows }],
      };
    }
    const cols = [
      { key: 'poNumber', label: 'PO #', align: 'text', width: '90px' },
      { key: 'vendorName', label: 'Vendor', align: 'text' },
      { key: 'orderedByFmt', label: 'Ordered By', align: 'text', width: '130px' },
      { key: 'createdFmt', label: 'Created', align: 'text', width: '120px' },
      { key: 'statusFmt', label: 'Status', align: 'text', width: '90px' },
      { key: 'totalFmt', label: 'Total', align: 'currency', width: '110px' },
    ];
    const rows = filteredRows.map((row) => ({
      poNumber: row.poNumber,
      vendorName: row.vendorName,
      orderedByFmt: row.orderedByName || row.orderedByEmail || '',
      createdFmt: formatDateTime(row.createdAt),
      statusFmt: getDeliveryStatusLabel(row.deliveryStatus),
      totalFmt: formatCurrency(row.total),
    }));
    return {
      store: { name: storeName },
      report: {
        title: 'Purchase Orders Report',
        subtitle: formatWrittenDate(data.generatedAt),
        counters: [],
        createdBy: createdBy || '',
        generatedAt: data.generatedAt,
        meta: [
          { label: 'Purchase Orders', value: formatNumber(data.totalOrders) },
          { label: 'Filtered Results', value: formatNumber(filteredRows.length) },
        ],
      },
      stats: [
        { label: 'Purchase Orders', value: formatNumber(data.totalOrders) },
        { label: 'Total Value', value: formatCurrency(data.totalValue) },
        { label: 'Filtered Results', value: formatNumber(filteredRows.length) },
        { label: 'Consolidated Vendors', value: formatNumber((data.consolidatedVendors || []).length) },
      ],
      sections: [{ columns: cols, rows }],
    };
  }, [storeName, data, filteredRows, createdBy, showConsolidated]);

  useEffect(() => {
    if (!onFooterActions) return undefined;
    onFooterActions(
      <>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => emailReportSummary(poDescriptor, [`Total value: ${formatCurrency(data.totalValue)}`, `Orders: ${formatNumber(filteredRows.length)}`])}>Email</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => downloadReportCsv(poDescriptor, `purchase_orders_${new Date().toISOString().slice(0, 10)}.csv`)}>Download CSV</button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => printReportDocument(poDescriptor)}>Print</button>
      </>
    );
    return () => onFooterActions(null);
  }, [poDescriptor, onFooterActions, data.totalValue, filteredRows.length]);

  return (
    <>
      <div className="card mb-3">
        <div className="card-body">
          {feedbackMessage ? <div className="alert alert-info py-2 mb-3">{feedbackMessage}</div> : null}
          <div className="row g-2 mb-2">
            <div className="col-lg-3">
              <label className="form-label">Vendor</label>
              <input className="form-control" value={vendorSearch} onChange={(event) => setVendorSearch(event.target.value)} placeholder="Search vendor" />
            </div>
            <div className="col-lg-3">
              <label className="form-label">Ordered By</label>
              <input className="form-control" value={orderedBySearch} onChange={(event) => setOrderedBySearch(event.target.value)} placeholder="User name/email" />
            </div>
            <div className="col-lg-2">
              <label className="form-label">Status</label>
              <select className="form-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">All</option>
                {deliveryStatuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div className="col-lg-2">
              <label className="form-label">From</label>
              <input type="date" className="form-control" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
            </div>
            <div className="col-lg-2">
              <label className="form-label">To</label>
              <input type="date" className="form-control" value={toDate} onChange={(event) => setToDate(event.target.value)} />
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex gap-2 align-items-center">
              <label className="form-label mb-0">Sort</label>
              <select className="form-select form-select-sm" style={{ width: 190 }} value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                <option value="created_desc">Newest first</option>
                <option value="total_desc">Total high to low</option>
                <option value="total_asc">Total low to high</option>
                <option value="vendor_asc">Vendor A-Z</option>
              </select>
            </div>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowConsolidated((previous) => !previous)}>
              {showConsolidated ? 'Show Individual POs' : 'Show Consolidated by Vendor'}
            </button>
          </div>
        </div>
      </div>

      <ReportDocument
        descriptor={poDescriptor}
        onDownload={() => downloadReportCsv(poDescriptor, `purchase_orders_${new Date().toISOString().slice(0, 10)}.csv`)}
        onEmail={() => emailReportSummary(poDescriptor, [`Total value: ${formatCurrency(data.totalValue)}`, `Orders: ${formatNumber(filteredRows.length)}`])}
      >
        {!showConsolidated && filteredRows.length > 0 && (
          <div className="mt-3">
            <div className="small text-muted fw-semibold mb-2" style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Quick Actions</div>
            {isMobile ? (
              <div className="d-flex flex-column gap-2" style={{ position: 'relative' }}>
                {expandedPoId !== null && (
                  <div
                    className="po-mobile-backdrop"
                    onClick={() => setExpandedPoId(null)}
                    aria-hidden="true"
                  />
                )}
                {filteredRows.map((row) => {
                  const emailStatus = getPoEmailStatusInfo(row);
                  const iconColorMap = { red: '#D56B61', open: '#7EA64E', closed: '#5F8EC4', neutral: '#A89890' };
                  const iconColor = iconColorMap[emailStatus.variant] || '#A89890';
                  const isExpanded = expandedPoId === row.id;
                  const isCopied = copiedPoId === row.id;
                  const displayName = row.orderedByName || row.orderedByEmail || '';
                  const dateStr = row.createdAt ? formatDateTime(row.createdAt) : '';
                  return (
                    <div key={row.id} className="po-mobile-card" onClick={isExpanded ? () => setExpandedPoId(null) : undefined}>
                      <div className="po-mobile-card-header">
                        <span className="po-mobile-vendor">{row.vendorName || '—'}</span>
                        <span
                          className="po-mobile-status-icon"
                          title={emailStatus.tooltipLines.join('\n') || undefined}
                          style={{ color: iconColor, cursor: emailStatus.tooltipLines.length ? 'help' : 'default' }}
                        >
                          {emailStatus.icon}
                          {emailStatus.iconBadge ? <sup style={{ fontSize: '0.55em', fontWeight: 700, marginLeft: 1 }}>{emailStatus.iconBadge}</sup> : null}
                        </span>
                        <span className="badge text-bg-light border" style={{ fontSize: '0.72rem' }}>{getDeliveryStatusLabel(row.deliveryStatus)}</span>
                      </div>
                      <div className="po-mobile-meta">
                        {dateStr && <span>{dateStr}</span>}
                        {dateStr && displayName && <span className="po-mobile-meta-sep">·</span>}
                        {displayName && <span>{displayName}</span>}
                        {(dateStr || displayName) && <span className="po-mobile-meta-sep">·</span>}
                        <span className="po-mobile-total">{formatCurrency(row.total)}</span>
                      </div>
                      <div className="po-mobile-po-row">
                        <span className="po-mobile-po-number">{row.poNumber || '—'}</span>
                        <button
                          type="button"
                          className="po-mobile-expand-btn"
                          aria-label="Show full PO number"
                          onClick={() => setExpandedPoId(isExpanded ? null : row.id)}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="po-mobile-popover" role="dialog" aria-label="Full PO number" onClick={(e) => e.stopPropagation()}>
                          <div className="po-mobile-popover-inner">
                            <div className="po-mobile-popover-label">PO Number</div>
                            <div className="po-mobile-popover-value">{row.poNumber}</div>
                            <div className="po-mobile-popover-actions">
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                style={{ fontSize: '0.78rem' }}
                                onClick={() => {
                                  if (!navigator.clipboard) return;
                                  navigator.clipboard.writeText(row.poNumber || '').then(() => {
                                    setCopiedPoId(row.id);
                                    setTimeout(() => setCopiedPoId((prev) => (prev === row.id ? null : prev)), 1800);
                                  }).catch(() => {});
                                }}
                              >
                                {isCopied ? 'Copied!' : 'Copy'}
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                style={{ fontSize: '0.78rem' }}
                                onClick={() => setExpandedPoId(null)}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="po-mobile-actions">
                        <button
                          type="button"
                          className="btn btn-outline-primary po-mobile-action-btn"
                          onClick={() => setSelectedPo(row)}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary po-mobile-action-btn"
                          onClick={() => void handleResend(row.id)}
                          disabled={resendingId === row.id}
                        >
                          {resendingId === row.id ? 'Resending…' : 'Resend'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="d-flex flex-column gap-1">
                {filteredRows.map((row) => {
                  const emailStatus = getPoEmailStatusInfo(row);
                  const iconColorMap = { red: '#D56B61', open: '#7EA64E', closed: '#5F8EC4', neutral: '#A89890' };
                  const iconColor = iconColorMap[emailStatus.variant] || '#A89890';
                  return (
                    <div key={row.id} className="d-flex align-items-center gap-2 flex-wrap py-1" style={{ borderBottom: '1px solid #f0ece8' }}>
                      <span className="small text-muted" style={{ minWidth: 80, fontVariantNumeric: 'tabular-nums' }}>{row.poNumber}</span>
                      <span className="small fw-semibold" style={{ minWidth: 120 }}>{row.vendorName}</span>
                      <span
                        title={emailStatus.tooltipLines.join('\n') || undefined}
                        style={{ fontSize: '0.95rem', color: iconColor, cursor: emailStatus.tooltipLines.length ? 'help' : 'default', userSelect: 'none' }}
                      >
                        {emailStatus.icon}
                        {emailStatus.iconBadge ? <sup style={{ fontSize: '0.55em', fontWeight: 700, marginLeft: 1 }}>{emailStatus.iconBadge}</sup> : null}
                      </span>
                      <span className="badge text-bg-light border small">{getDeliveryStatusLabel(row.deliveryStatus)}</span>
                      <span className="ms-auto d-flex gap-1">
                        <button type="button" className="btn btn-outline-primary btn-sm py-0 px-2" style={{ fontSize: '0.78rem' }} onClick={() => setSelectedPo(row)}>Preview</button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm py-0 px-2"
                          style={{ fontSize: '0.78rem' }}
                          onClick={() => void handleResend(row.id)}
                          disabled={resendingId === row.id}
                        >
                          {resendingId === row.id ? 'Resending…' : 'Resend'}
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {selectedPreview ? (() => {
          const po = selectedPreview.purchaseOrder;
          const previewBody = (
            <>
              <div className="vos-preview-meta">
                <div style={{ overflowWrap: 'anywhere' }}><strong>Vendor:</strong> {po.vendorName}</div>
                <div style={{ overflowWrap: 'anywhere' }}><strong>Recipient:</strong> {po.contact.contactName || 'Not set'}</div>
                <div style={{ overflowWrap: 'anywhere' }}><strong>Email:</strong> {po.contact.contactEmail || 'Not set'}</div>
              </div>
              <div className="vos-preview-subject"><strong>Subject:</strong> {selectedPreview.emailPreview.subject}</div>
              <pre style={{ whiteSpace: 'pre-wrap', overflowX: 'hidden' }}>{selectedPreview.emailPreview.text}</pre>
            </>
          );
          if (isMobile) {
            return (
              <MobileReportPopup
                title={`${po.poNumber} Preview`}
                onClose={() => setSelectedPo(null)}
              >
                {previewBody}
              </MobileReportPopup>
            );
          }
          return (
            <div className="vos-preview-overlay" onClick={() => setSelectedPo(null)}>
              <div className="vos-preview" onClick={(event) => event.stopPropagation()}>
                <div className="vos-preview-header">
                  <strong>{po.poNumber} Preview</strong>
                  <button type="button" onClick={() => setSelectedPo(null)}>&times;</button>
                </div>
                {previewBody}
              </div>
            </div>
          );
        })() : null}

        {activePoRow && (
          <RowDetailsSheet
            title={activePoRow._isConsolidated ? activePoRow.vendorName : (activePoRow.poNumber || 'Purchase Order')}
            fields={activePoRow._isConsolidated ? [
              { label: 'Vendor', value: activePoRow.vendorName, wide: true },
              { label: 'Purchase Orders', value: formatNumber(activePoRow.orderCount) },
              { label: 'Line Items', value: formatNumber(activePoRow.lineCount) },
              { label: 'Total Value', value: formatCurrency(activePoRow.total) },
            ] : [
              { label: 'PO #', value: activePoRow.poNumber, wide: true },
              { label: 'Vendor', value: activePoRow.vendorName, wide: true },
              { label: 'Ordered By', value: activePoRow.orderedByName || 'Unknown User' },
              { label: 'Email', value: activePoRow.orderedByEmail || '-' },
              { label: 'Created', value: formatDateTime(activePoRow.createdAt) },
              { label: 'Status', value: getDeliveryStatusLabel(activePoRow.deliveryStatus) },
              { label: 'Total', value: formatCurrency(activePoRow.total) },
            ]}
            actions={!activePoRow._isConsolidated ? [
              <button
                key="preview"
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => { setSelectedPo(activePoRow); setActivePoRow(null); }}
              >
                Preview
              </button>,
              <button
                key="resend"
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => { void handleResend(activePoRow.id); setActivePoRow(null); }}
                disabled={resendingId === activePoRow.id}
              >
                {resendingId === activePoRow.id ? 'Resending...' : 'Resend'}
              </button>,
            ] : null}
            onClose={() => setActivePoRow(null)}
          />
        )}

      <style>{`
          .po-mobile-backdrop {
            position: fixed;
            inset: 0;
            z-index: 10;
          }
          .po-mobile-card {
            background: #fdf9f6;
            border: 1px solid #e8ddd6;
            border-radius: 10px;
            padding: 0.75rem 0.875rem;
            position: relative;
            z-index: 11;
          }
          .po-mobile-card-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: nowrap;
          }
          .po-mobile-vendor {
            font-weight: 600;
            font-size: 0.92rem;
            color: #2d1f17;
            flex: 1 1 0;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .po-mobile-status-icon {
            font-size: 1rem;
            flex-shrink: 0;
            user-select: none;
          }
          .po-mobile-meta {
            display: flex;
            align-items: center;
            gap: 0.3rem;
            font-size: 0.78rem;
            color: #7a6860;
            margin-top: 0.2rem;
            flex-wrap: wrap;
          }
          .po-mobile-meta-sep {
            color: #b8a89e;
          }
          .po-mobile-total {
            font-weight: 600;
            color: #4a3f38;
          }
          .po-mobile-po-row {
            display: flex;
            align-items: center;
            gap: 0.35rem;
            margin-top: 0.35rem;
          }
          .po-mobile-po-number {
            font-size: 0.77rem;
            color: #8a7970;
            font-variant-numeric: tabular-nums;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1 1 0;
            min-width: 0;
          }
          .po-mobile-expand-btn {
            background: none;
            border: none;
            padding: 0 0.2rem;
            font-size: 0.6rem;
            color: #a89890;
            cursor: pointer;
            flex-shrink: 0;
            line-height: 1;
          }
          .po-mobile-expand-btn:hover {
            color: #5a4a42;
          }
          .po-mobile-popover {
            margin-top: 0.5rem;
            background: #fff;
            border: 1px solid #ddd3c8;
            border-radius: 8px;
            padding: 0.6rem 0.75rem;
          }
          .po-mobile-popover-label {
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #a89890;
            margin-bottom: 0.2rem;
          }
          .po-mobile-popover-value {
            font-size: 0.88rem;
            font-variant-numeric: tabular-nums;
            color: #2d1f17;
            word-break: break-all;
            margin-bottom: 0.5rem;
          }
          .po-mobile-popover-actions {
            display: flex;
            gap: 0.4rem;
          }
          .po-mobile-actions {
            display: flex;
            gap: 0.5rem;
            margin-top: 0.65rem;
          }
          .po-mobile-action-btn {
            flex: 1 1 0;
            min-height: 44px;
            font-size: 0.85rem;
            font-weight: 500;
          }
          .vos-preview-overlay {
            position: fixed;
            inset: 0;
            z-index: 11100;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: calc(env(safe-area-inset-top, 0px) + 0.8rem);
            padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 0.8rem);
            padding-left: 0.8rem;
            padding-right: 0.8rem;
          }
          .vos-preview {
            width: min(680px, 100%);
            max-height: 88vh;
            overflow-y: auto;
            background: #fff;
            border-radius: 12px;
            border: 1px solid #e6dbd1;
            padding: 0.9rem;
          }
          .vos-preview-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            color: #3d2518;
          }
          .vos-preview-header button {
            border: none;
            background: none;
            font-size: 1.4rem;
            color: #8b7b73;
            line-height: 1;
          }
          .vos-preview-meta {
            margin-top: 0.45rem;
            font-size: 0.8rem;
            color: #5a4a42;
          }
          .vos-preview-subject {
            margin-top: 0.55rem;
            font-size: 0.78rem;
            color: #5a4a42;
          }
          .vos-preview pre {
            white-space: pre-wrap;
            font-size: 0.74rem;
            background: #faf6f2;
            border: 1px solid #eee3d8;
            border-radius: 8px;
            padding: 0.75rem;
            color: #4b3f37;
            margin: 0.55rem 0 0;
          }
      `}</style>
    </ReportDocument>
  </>
  );
};

export default Reports;
